import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert, escapeHtml } from "./utils/dNews_Alert";
import Select from "react-select";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as ExcelJS from "exceljs";
import { apiFetch } from "./utils/api";
import { exportTicketsPdf } from "./utils/ticketExport";
import ExportMenu from "./ExportMenu";
import PayslipPreviewBody from "./PayslipPreviewBody";
import { dslipYearsToDate } from "./utils/dSlip_years";
import { numericInputProps } from "./utils/numericInput";
import { addEagleWatermark } from "./utils/pdfWatermark";
import { PAYSLIP_FONTS, DEFAULT_FONT_KEY, fontCssStyle, getFont } from "./utils/payslipFonts";
import { FaTimes, FaEye, FaEdit, FaTrash, FaCheckCircle, FaCheckDouble, FaFilePdf, FaFileExcel, FaPlus, FaEnvelope, FaRegEnvelopeOpen, FaPaperPlane, FaHistory } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import signature_img from "./assets/img/signature.png";
import DOLLUZ_CORP from "./assets/img/DOLLUZ_CORP.png";
import DOLLUZ_Full_Logo from "./assets/img/DOLLUZ_Full_Logo.png";
import "./Payslip_Management.css";

// Cap per-component amount (matches Salary Structure's MAX_AMOUNT)
const MAX_AMOUNT_PM = 99999999;

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const now = new Date();
const DEFAULT_MONTH = now.getMonth() === 0 ? 12 : now.getMonth();
const DEFAULT_YEAR = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

// 2018 .. current year, newest first. No future years - there are no payslips
// to manage for a month that hasn't happened yet.
const YEAR_OPTIONS = dslipYearsToDate();

// Shared react-select styling so From/To dropdowns match Employee Center
// look (rounded card, slate border, orange focus ring). menuPortal lifts
// the menu above the sticky-headed table further down the page.
const MONTH_SELECT_OPTIONS = MONTH_NAMES.slice(1).map((m, i) => ({ value: i + 1, label: m }));
const YEAR_SELECT_OPTIONS = YEAR_OPTIONS.map(y => ({ value: y, label: String(y) }));
const PM_SELECT_STYLES = {
    control: (p, s) => ({
        ...p,
        minHeight: 40,
        borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
        padding: "10px 12px",
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
};
const PM_MONTH_STYLES = { ...PM_SELECT_STYLES, container: p => ({ ...p, width: 132 }) };
const PM_YEAR_STYLES  = { ...PM_SELECT_STYLES, container: p => ({ ...p, width: 104 }) };
const PM_EMP_STYLES   = { ...PM_SELECT_STYLES, container: p => ({ ...p, width: "100%" }) };
// Recipient jump list inside the email modal. Same control as every other
// dropdown on the page; only the width and a menu z-index above the modal
// overlay differ.
const PM_RCPT_STYLES  = {
    ...PM_SELECT_STYLES,
    container: p => ({ ...p, width: 210 }),
    control: (p, s) => ({ ...PM_SELECT_STYLES.control(p, s), minHeight: 34 }),
    menuPortal: base => ({ ...base, zIndex: 100000 }),
};
// Email-history filters. Same control, sized for the modal's filter row and
// portalled above the overlay.
// Filter bar shared by the Publish All and Remind All pickers. Same controls
// as the page's own filter bar (employee + From/To period), scoped to the list
// inside the modal. Narrowing here only changes what is SHOWN - it never
// silently unticks a row, so the footer count stays truthful.
function PickerFilters({ rows, filters, onChange, empOptions }) {
    const dirty = filters.emp_id !== "";
    return (
        <div className="pm-pickfilter">
            <div className="pm-pickfilter-field">
                <label>Employee</label>
                <Select
                    options={empOptions}
                    value={empOptions.find(o => o.value === filters.emp_id) || empOptions[0]}
                    onChange={opt => onChange({ ...filters, emp_id: opt?.value || "" })}
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={PM_PICK_EMP_STYLES}
                />
            </div>
            <div className="pm-pickfilter-field">
                <label>From</label>
                <div className="pm-pickfilter-range">
                    <Select
                        options={MONTH_SELECT_OPTIONS}
                        value={MONTH_SELECT_OPTIONS.find(o => o.value === filters.fromMonth)}
                        onChange={opt => onChange({ ...filters, fromMonth: opt.value })}
                        isSearchable={false} menuPortalTarget={document.body} styles={PM_PICK_MONTH_STYLES}
                    />
                    <Select
                        options={YEAR_SELECT_OPTIONS}
                        value={YEAR_SELECT_OPTIONS.find(o => o.value === filters.fromYear)}
                        onChange={opt => onChange({ ...filters, fromYear: opt.value })}
                        isSearchable={false} menuPortalTarget={document.body} styles={PM_PICK_YEAR_STYLES}
                    />
                </div>
            </div>
            <div className="pm-pickfilter-field">
                <label>To</label>
                <div className="pm-pickfilter-range">
                    <Select
                        options={MONTH_SELECT_OPTIONS}
                        value={MONTH_SELECT_OPTIONS.find(o => o.value === filters.toMonth)}
                        onChange={opt => onChange({ ...filters, toMonth: opt.value })}
                        isSearchable={false} menuPortalTarget={document.body} styles={PM_PICK_MONTH_STYLES}
                    />
                    <Select
                        options={YEAR_SELECT_OPTIONS}
                        value={YEAR_SELECT_OPTIONS.find(o => o.value === filters.toYear)}
                        onChange={opt => onChange({ ...filters, toYear: opt.value })}
                        isSearchable={false} menuPortalTarget={document.body} styles={PM_PICK_YEAR_STYLES}
                    />
                </div>
            </div>
            {dirty && (
                <button type="button" className="pm-maillog-reset" onClick={() => onChange({ ...filters, emp_id: "" })}>
                    Clear
                </button>
            )}
            <span className="pm-pickfilter-count">{rows.length} shown</span>
        </div>
    );
}

const PM_LOG_PORTAL        = { menuPortal: b => ({ ...b, zIndex: 100000 }) };
const PM_LOG_MONTH_STYLES  = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, container: p => ({ ...p, width: 124 }) };
const PM_LOG_YEAR_STYLES   = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, container: p => ({ ...p, width: 96 }) };
const PM_LOG_EMP_STYLES    = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, container: p => ({ ...p, width: 216 }) };
// Picker filter bar - compact variants of the same control.
const PM_PICK_CTRL = (p, s) => ({ ...PM_SELECT_STYLES.control(p, s), minHeight: 34 });
// Widths match the page's own filter bar (month 132 / year 104) so the year
// never truncates to "20…" - react-select ellipsises the value, it does not
// shrink the text.
const PM_PICK_EMP_STYLES   = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, control: PM_PICK_CTRL, container: p => ({ ...p, width: 230 }) };
const PM_PICK_MONTH_STYLES = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, control: PM_PICK_CTRL, container: p => ({ ...p, width: 132 }) };
const PM_PICK_YEAR_STYLES  = { ...PM_SELECT_STYLES, ...PM_LOG_PORTAL, control: PM_PICK_CTRL, container: p => ({ ...p, width: 104 }) };

// Human-readable range label: "February 2026" if single month, else "February 2026 - April 2026"
const rangeLabel = (fy, fm, ty, tm) =>
    (fy === ty && fm === tm)
        ? `${MONTH_NAMES[fm]} ${fy}`
        : `${MONTH_NAMES[fm]} ${fy} - ${MONTH_NAMES[tm]} ${ty}`;

// Compare two (year, month) tuples; positive => a after b
const cmpYM = (ay, am, by, bm) => (ay - by) || (am - bm);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const EMPTY_EDIT = {
    lop_days: "0",
    bonus: "0",
    other_earnings: "0",
    other_earnings_note: "",
    loan_deduction: "0",
    other_deductions: "0",
    other_deductions_note: "",
};

// Recalculate payslip values for edit preview - mirrors backend logic exactly.
// Earnings & deductions come from frozen payslip_item snapshots; admin only
// edits adjustments (lop_days, lop_deduction override, bonus, loan, other).
function recalc(payslip, edit) {
    const daysInMonth = payslip.days_in_month || 30;
    const lopDays = parseFloat(edit.lop_days) || 0;
    const daysWorked = daysInMonth - lopDays;

    // Source of truth: editable items when present (admin is overriding),
    // else the frozen payslip_item snapshots.
    const editEarn = Array.isArray(edit.editEarnings) ? edit.editEarnings : null;
    const editDed  = Array.isArray(edit.editDeductions) ? edit.editDeductions : null;
    const items = Array.isArray(payslip.items) ? payslip.items : [];
    const earnItems = editEarn ?? items.filter((i) => i.component_type === "Earning");
    const dedItems  = editDed  ?? items.filter((i) => i.component_type === "Deduction");

    const grossEarn = earnItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const structDed = dedItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

    const bonus = parseFloat(edit.bonus) || 0;
    const otherEarn = parseFloat(edit.other_earnings) || 0;
    const loanDed = parseFloat(edit.loan_deduction) || 0;
    const otherDed = parseFloat(edit.other_deductions) || 0;

    // Auto-LOP suggestion from snapshot Basic; admin override wins
    const basicItem = earnItems.find((i) => i.component_name === "Basic");
    const basicAmt = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
    const lopSuggested = lopDays > 0
        ? parseFloat(((basicAmt / daysInMonth) * lopDays).toFixed(2)) : 0;
    const lopOverride = edit.lop_deduction;
    const lopDed = (lopOverride !== undefined && lopOverride !== "" && lopOverride !== null)
        ? (parseFloat(lopOverride) || 0)
        : lopSuggested;

    const gross = grossEarn + bonus + otherEarn;
    const totalDed = structDed + lopDed + loanDed + otherDed;
    const netPay = gross - totalDed;

    return {
        daysInMonth, lopDays, daysWorked,
        earnItems, dedItems,
        grossEarn, structDed,
        bonus, otherEarn, loanDed, otherDed,
        lopDed, lopSuggested,
        gross, totalDed, netPay,
    };
}

// ─── PDF generator ────────────────────────────────────────────────────────────


function generatePayslipPDF(p) {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210;
    const margin = 6;                // narrow side margins so the tables fill the page (was 14mm)
    const innerW = W - margin * 2;   // 198mm

    // ── Resolve the chosen font preset once and re-use it everywhere ──
    // Every setFont call below uses FONT_FAM for the family, and remaps the
    // semantic style ("normal"/"bold") through fontStyle()/boldStyle() so
    // presets like "helvetica_bold" or "times_italic" stay visually
    // distinct without us having to touch each call site individually.
    const FONT = getFont(p.font);
    const FONT_FAM = FONT.pdfFamily;
    const fontStyle = (semantic) => {
        if (semantic === "bold") return FONT.pdfStyle === "italic" ? "bolditalic" : "bold";
        // semantic "normal" - honor the preset's own style for italic / bold variants
        return FONT.pdfStyle || "normal";
    };

    // ── Filename: DZIND106_Nov'25.pdf ────────────────────────
    const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthAbbr = MONTH_SHORT[p.pay_month - 1];
    const yearShort = String(p.pay_year).slice(-2);
    const filename = `${p.emp_id}_${monthAbbr}'${yearShort}.pdf`;

    // ── Number helpers ───────────────────────────────────────
    // Integer with commas: 37,726  (no .00)
    const ni = (v) => Math.round(Number(v || 0)).toLocaleString("en-IN");

    const formatDOJ = (d) => {
        if (!d) return "\u2014";
        const dt = new Date(d);
        if (isNaN(dt)) return "\u2014";
        return `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}`;
    };

    // Use live name if joined by detail endpoint, fallback to snapshot
    const empName = (`${p.emp_first_name || ""} ${p.emp_last_name || ""}`.trim()) || p.emp_name || "";

    // Snapshot fields with legacy-column fallbacks
    const panNumber = p.pan_number || "-";
    const empCode = p.emp_code || p.emp_id || "-";
    const location = p.location || p.emp_location || "-";
    const designation = p.designation || p.job_position || "-";
    const department = p.department || p.emp_department || "-";
    const bankDetails = p.bank_details || p.bank_account_number || "-";

    // ── 1. Top yellow accent - sits a little below the page edge so there's
    //    a strip of white space above it (matches the reference).
    const yellowSide = 8;
    doc.setFillColor(255, 237, 0);   // bright yellow (not gold)
    doc.rect(yellowSide, 1, W - yellowSide * 2, 2.6, "F");

    // ── 2. Full DOLLUZ CORP logo - eagle + wordmark + tagline baked into one
    //    image (DOLLUZ_Full_Logo.png, ~square), used as the whole letterhead.
    const logoW = 40, logoH = logoW / 0.992;   // image aspect 1932x1947
    const logoY = 11;
    try { doc.addImage(DOLLUZ_Full_Logo, "PNG", (W - logoW) / 2, logoY, logoW, logoH); } catch (_) { }

    // ── 3. Title - a little below the logo (clear gap from the logo)
    const titleY = logoY + logoH + 14;
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(
        `PAYSLIP FOR THE MONTH - ${MONTH_NAMES[p.pay_month].toUpperCase()} ${p.pay_year}`,
        W / 2, titleY, { align: "center" }
    );
    const empStartY = titleY + 6;

    // =====================================================
    // 4. EMPLOYEE INFO - 4-col bordered table (exact sample layout)
    //    | NAME:          | Prasanth M           | PAN:          | AVWPP8650N  |
    //    | EMPLOYEE CODE: | DZIND106             | LOCATION:     | Chennai     |
    //    | DESIGNATION:   | Int. Coding Spec.    | BANK DETAILS: | 91701...    |
    //    | DEPARTMENT:    | Int. Coding Ops      | DAYS PAYABLE: | 30          |
    //    | DOJ:           | 2019/12/02           | LOP:          | 0           |
    // =====================================================
    const EMP_FONT_SIZE = 11.5;
    const empRows = [
        ["NAME:", empName, "PAN:", panNumber],
        ["EMPLOYEE CODE:", empCode, "LOCATION:", location],
        ["DESIGNATION:", designation, "BANK DETAILS:", bankDetails],
        ["DEPARTMENT:", department, "DAYS PAYABLE:", String(p.days_in_month || 30)],
        // LOP renders as integer when no fractional part (so "0" not "0.0")
        ["DOJ:", formatDOJ(p.doj), "LOP:", (() => {
            const n = parseFloat(p.lop_days) || 0;
            return n % 1 === 0 ? String(parseInt(n, 10)) : String(n);
        })()],
    ];

    // Pin each label column to the width of its widest label.
    //
    // This used to use cellWidth:"wrap" on the assumption that it makes a
    // column hug its text. It does not when tableWidth is fixed - autoTable
    // still hands "wrap" columns a share of the leftover width. Measured on the
    // real table: column 0 came out 46.9mm when its widest label
    // ("EMPLOYEE CODE:") only needs 38.6mm, and column 2 came out 40.4mm
    // against 33.2mm needed. That 8.3mm + 7.2mm of dead space inside the label
    // cells is what pushed every value far away from its label.
    //
    // Measuring against the live font/size keeps this correct for every font
    // preset rather than hard-coding millimetres.
    const LABEL_PAD_L = 2;     // left padding applied in didParseCell below
    const VALUE_PAD_L = 0.1;   // left padding applied to value cells below
    const VALUE_PAD_R = 2;
    const COL_GUTTER = 8;      // white band between the left value and the right label
    // Label -> value breathing room, tuned per half rather than shared. The
    // right half reads tighter at the same measurement because TWO of its
    // labels ("BANK DETAILS:", "DAYS PAYABLE:") sit within a millimetre of the
    // column's widest, so the minimum gap shows up on two rows instead of one.
    // A slightly wider gap there makes the two halves look even.
    const LABEL_GAP_L = 3.2;
    const LABEL_GAP_R = 4.4;
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.setFontSize(EMP_FONT_SIZE);
    const colTextW = (i) => Math.max(...empRows.map(r => doc.getTextWidth(String(r[i]))));
    const label0W = colTextW(0) + LABEL_PAD_L + LABEL_GAP_L;
    const label2W = colTextW(2) + LABEL_PAD_L + LABEL_GAP_R;

    // Size the left VALUE column to its content plus a fixed gutter, rather
    // than leaving it "auto". Left as "auto" it swallowed the whole surplus
    // (86.9mm for 62.2mm of text), which parked the right-hand labels 24.7mm
    // clear of the longest left value - a much wider band than the label gap
    // on the left, so the two halves looked unbalanced.
    //
    // Content-driven rather than a fixed 50/50 split because an even split
    // gives this column 57.2mm while "Software Development / DevOps" needs
    // 62.2mm - it would wrap. Clamped so an unusually long designation or
    // department can still never squeeze the right value column below its own
    // longest value. Column 3 stays "auto" and absorbs whatever is left.
    const col3Min = colTextW(3) + VALUE_PAD_L + VALUE_PAD_R;
    const col1Max = innerW - label0W - label2W - col3Min;
    const col1W = Math.min(colTextW(1) + VALUE_PAD_L + COL_GUTTER, col1Max);

    // Captured while the emp table draws: the Y of the "LOCATION" row (row
    // index 1, EMPLOYEE CODE / LOCATION). The watermark is anchored to start
    // here so every payslip's mark begins on the same line.
    let locationRowY = null;

    autoTable(doc, {
        startY: empStartY,
        body: empRows,
        theme: "grid",
        margin: { left: margin, right: margin },
        tableWidth: innerW,
        didDrawCell(data) {
            if (data.section === "body" && data.row.index === 1 && data.column.index === 0) {
                locationRowY = data.cell.y;
            }
        },
        bodyStyles: {
            font: FONT_FAM,
            fontSize: EMP_FONT_SIZE,
            cellPadding: { top: 1.8, bottom: 1.8, left: LABEL_PAD_L, right: 1 },
            textColor: [25, 25, 25],
        },
        // Small gap between each label and its value, plus extra top/bottom on
        // the first/last rows so the block isn't cramped above NAME: / below DOJ:.
        didParseCell(data) {
            if (data.section !== "body") return;
            const isLabel = data.column.index === 0 || data.column.index === 2;
            const lr = isLabel ? { left: LABEL_PAD_L, right: 0 } : { left: VALUE_PAD_L, right: VALUE_PAD_R };
            let top = 1.8, bottom = 1.8;
            if (data.row.index === 0) top = 6;
            else if (data.row.index === 4) bottom = 6;
            data.cell.styles.cellPadding = { top, bottom, ...lr };
        },
        // Labels AND values both bold (same weight), same size.
        columnStyles: {
            0: { cellWidth: label0W, fontStyle: "bold", fontSize: EMP_FONT_SIZE, textColor: [20, 20, 25] },
            1: { cellWidth: col1W, fontStyle: "bold", fontSize: EMP_FONT_SIZE, textColor: [20, 20, 20] },
            2: { cellWidth: label2W, fontStyle: "bold", fontSize: EMP_FONT_SIZE, textColor: [20, 20, 25] },
            3: { cellWidth: "auto", fontStyle: "bold", fontSize: EMP_FONT_SIZE, textColor: [20, 20, 20] },
        },
        // Cell-level lineWidth: 0 strips internal grid lines; the outer dark
        // rectangle is drawn by tableLineWidth at the table level.
        styles: { font: FONT_FAM, lineColor: [30, 30, 40], lineWidth: 0 },
        tableLineColor: [30, 30, 40],
        tableLineWidth: 0.7,
    });

    const empTableEnd = doc.lastAutoTable.finalY + 5;

    // =====================================================
    // 5. EARNINGS + DEDUCTIONS - single 4-column table
    //    Matching sample: yellow headers, no .00, integer amounts
    // =====================================================
    const eLabel = innerW * 0.37;   // wider label col so bold component names don't wrap
    const eAmt = innerW * 0.13;
    const dLabel = innerW * 0.37;
    const dAmt = innerW * 0.13;

    // Build earnings list from frozen payslip_item snapshots + per-month adjustments
    const snapshotItems = Array.isArray(p.items) ? p.items : [];
    const eItems = snapshotItems
        .filter(i => i.component_type === "Earning")
        .map(i => [i.component_name, ni(i.amount)]);
    if (parseFloat(p.bonus) > 0) eItems.push(["Bonus", ni(p.bonus)]);
    if (parseFloat(p.other_earnings) > 0) eItems.push([p.other_earnings_note || "Other Earnings", ni(p.other_earnings)]);

    // Build deductions list (snapshot + adjustments)
    const dItems = snapshotItems
        .filter(i => i.component_type === "Deduction")
        .map(i => [i.component_name, ni(i.amount)]);
    if (parseFloat(p.lop_deduction) > 0) dItems.push(["LOP Deduction", ni(p.lop_deduction)]);
    if (parseFloat(p.loan_deduction) > 0) dItems.push(["Salary Advance (Rev)", ni(p.loan_deduction)]);
    if (parseFloat(p.other_deductions) > 0) dItems.push([p.other_deductions_note || "Other Deductions", ni(p.other_deductions)]);

    // Zip into 4-column rows
    const maxRows = Math.max(eItems.length, dItems.length);
    const combinedRows = [];
    for (let i = 0; i < maxRows; i++) {
        const e = eItems[i] || ["", ""];
        const d = dItems[i] || ["", ""];
        combinedRows.push([e[0], e[1], d[0], d[1]]);
    }
    // Totals row (bold)
    combinedRows.push(["Total Earnings", ni(p.gross_salary), "Total Deductions", ni(p.total_deductions)]);
    const totalsRowIndex = combinedRows.length - 1;

    const YELLOW = [255, 215, 0];

    autoTable(doc, {
        startY: empTableEnd,
        head: [["Earnings", "Monthly\nin INR", "Deductions", "Monthly\nin INR"]],
        body: combinedRows,
        theme: "grid",
        margin: { left: margin, right: margin },
        tableWidth: innerW,
        headStyles: {
            font: FONT_FAM,
            fillColor: YELLOW,
            textColor: [20, 20, 20],
            fontStyle: "bold",
            fontSize: 14,
            halign: "left",
            valign: "middle",
            cellPadding: { top: 3, bottom: 3, left: 3, right: 2 },
        },
        bodyStyles: {
            font: FONT_FAM,
            fontSize: 14,
            textColor: [25, 25, 25],
            cellPadding: { top: 3, bottom: 3, left: 3, right: 2 },
        },
        columnStyles: {
            0: { cellWidth: eLabel },
            1: { cellWidth: eAmt, halign: "right", fontStyle: "normal" },   // amounts lighter than the names
            2: { cellWidth: dLabel },
            3: { cellWidth: dAmt, halign: "right", fontStyle: "normal" },
        },
        // didParseCell fires BEFORE layout, so setting fontSize here gives
        // the totals row the right cell height. Labels match the yellow
        // header (11.5 bold); values match the body amount size (10.5
        // normal) so all numbers in the table render uniformly.
        didParseCell(data) {
            if (data.section === "body" && data.row.index === totalsRowIndex) {
                // Totals row: labels (cols 0,2) bold, amounts (cols 1,3) normal.
                if (data.column.index === 0 || data.column.index === 2) {
                    data.cell.styles.fontSize = 14;
                    data.cell.styles.fontStyle = "bold";
                } else {
                    data.cell.styles.fontSize = 14;
                    data.cell.styles.fontStyle = "normal";   // totals amounts lighter, like the reference
                }
            } else if (data.section === "body" && data.row.index === totalsRowIndex - 1) {
                // A little extra breathing room below the last component row,
                // just above the Total Earnings / Total Deductions row.
                data.cell.styles.cellPadding = { top: 3, bottom: 6, left: 3, right: 2 };
            }
        },
        willDrawCell(data) {
            if (data.section === "head") {
                if (data.column.index === 1 || data.column.index === 3) {
                    data.cell.styles.halign = "center";
                }
                // Thick line UNDER the yellow header row; thin verticals.
                data.cell.styles.lineWidth = { top: 0, bottom: 1.2, left: 0.6, right: 0.6 };
                return;
            }
            if (data.section === "body" && data.row.index === totalsRowIndex) {
                // Totals row keeps ALL borders (top closes off data block,
                // bottom closes off the table). Sizes/weight already set
                // in didParseCell so layout is correct.
                data.cell.styles.fillColor = [248, 248, 248];
                return;
            }
            // All other body cells: strip every internal horizontal so the
            // earnings + deductions data flows like the canonical Dolluz PDF
            // (no grid lines between Basic / HRA / Medical / ...). ALL
            // verticals stay full-height - including the divider between
            // each side's label and amount columns, even through orphan
            // empty rows (mirrors the reference PDF).
            data.cell.styles.lineWidth = { top: 0, bottom: 0, left: 0.7, right: 0.7 };
        },
        // `styles.lineColor` applies to every cell border (header + body
        // verticals + totals row). Without this, autoTable falls back to its
        // default very-light-grey ~[200,200,200] and the internal dividers
        // basically disappear against white paper. `tableLineColor` only
        // covers the outer table border.
        styles: { lineColor: [30, 30, 40], lineWidth: 0.7 },
        tableLineColor: [30, 30, 40],
        tableLineWidth: 0.7,
    });

    const tableEndY = doc.lastAutoTable.finalY;

    // ── 6. Net Pay - label anchored at the start of the Deductions column.
    //    Label = 11.5pt bold (matches "Earnings" / "Total Earnings" / "Deductions").
    //    Value = 10.5pt NORMAL - same size + weight as totals-row values
    //    (1,17,326 / 17,237). Value uses HARDCODED "normal" so it isn't
    //    reverted to bold by font-style helpers under bold presets.
    const netY = tableEndY + 8;
    doc.setTextColor(20, 20, 20);
    // Label x matches the table's cellPadding.left (4mm) so "NET PAY" 'N'
    // sits exactly under "Total Deductions" 'T'.
    const netPayLabelX = margin + innerW * 0.36 + innerW * 0.14 + 3;
    // Value right edge matches the table's cellPadding.right (2mm) so
    // "1,00,189" ends at the same column as "17,137".
    const netPayValueRightX = W - margin - 2;

    // Label - bold, 14pt (same size as the whole table; headings/labels just bold)
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.setFontSize(14);
    doc.text("NET PAY", netPayLabelX, netY, { align: "left" });

    // Value - explicit "normal" cut, 14pt (matches body amounts + totals values)
    doc.setFont(FONT_FAM, "normal");
    doc.setFontSize(14);
    doc.text(ni(p.net_pay), netPayValueRightX, netY, { align: "right" });

    // ── 7. Note - bold "Note:" larger, sentence smaller + lighter for clear
    //    visual hierarchy. Sentence uses hardcoded "normal" so it doesn't
    //    inherit a bold cut under presets like helvetica_bold.
    const noteY = netY + 12;

    // "Note:" - 12.5pt bold, dark
    doc.setFontSize(12.5);
    doc.setTextColor(20, 20, 20);
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.text("Note:", margin, noteY);

    // Measure the bold "Note: " width WHILE the bold font is still active
    // so the sentence sits one natural space after, regardless of preset.
    const noteLabelWidth = doc.getTextWidth("Note: ");

    // Sentence - 11.5pt normal, lighter grey
    doc.setFontSize(11.5);
    doc.setFont(FONT_FAM, "normal");   // hardcoded "normal" - see comment above
    doc.setTextColor(90, 90, 90);
    doc.text(
        "Income Tax will be deducted as per income Tax Act 1961 and will be borne by the employee",
        margin + noteLabelWidth, noteY
    );

    // =====================================================
    // 8. FOOTER - hardcoded company name + verbatim address textarea +
    //    signature image on the right.
    // =====================================================
    // Address lines - computed up front so we can size the footer block and
    // guarantee a little white space below it (see footerY cap).
    const addressLines = (p.company_address || "#254/3, Sree Narayana Complex, C Block,\nSarathy Nagar, Velachery, Chennai 600042")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .slice(0, 6);

    // Keep a bottom margin so the footer/signature never sits flush to the page
    // edge on a tall (many-component) payslip. The block extends ~26mm below
    // footerY on the signature side, or 15 + (n-1)*5.5 on the address side - use
    // the taller. A short payslip keeps the natural spot right under the Note; a
    // tall one is pulled up to the cap (but never above the Note itself).
    const pageH = doc.internal.pageSize.getHeight();
    const BOTTOM_MARGIN = 10;
    const footerBlockH = Math.max(26, 15 + Math.max(0, addressLines.length - 1) * 5.5 + 4);
    const footerY = Math.max(
        noteY + 8,
        Math.min(noteY + 12, pageH - BOTTOM_MARGIN - footerBlockH)
    );

    // Hardcoded company name always renders first - soft grey, a touch larger.
    doc.setTextColor(55, 55, 55);
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.setFontSize(11);
    doc.text("Dolluz Corporation (OPC) Private Limited", margin, footerY + 10);

    doc.setFont(FONT_FAM, fontStyle("normal"));
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    addressLines.forEach((line, idx) => {
        doc.text(line, margin, footerY + 17 + idx * 5.5);
    });

    // Right - signature image above AUTHORIZED SIGNATURE label.
    // The signature is kept NARROWER than the label and centered over it,
    // so it sits compactly above the caption rather than overhanging it.
    const sigRight = W - margin - 3;                  // pulled in from the right edge a hair
    const sigLabelText = "AUTHORIZED SIGNATURE";
    doc.setFont(FONT_FAM, fontStyle("bold"));
    doc.setFontSize(10.5);                            // heavier + larger caption
    const sigLabelW = doc.getTextWidth(sigLabelText);
    const sigWidth = Math.min(54, sigLabelW * 1.05);  // a touch larger, ~label width
    const sigHeight = sigWidth / 5.0;                 // shorter than the natural aspect
    const sigLabelCenterX = sigRight - sigLabelW / 2; // label is right-aligned at sigRight
    const sigX = sigLabelCenterX - sigWidth / 2;      // centre the signature over the label
    const sigY = footerY + 8;

    try {
        doc.addImage(signature_img, "PNG", sigX, sigY, sigWidth, sigHeight);
    } catch (_) {
        // If image fails, draw blank space
    }

    // "AUTHORIZED SIGNATURE" bold label below the signature image (extra gap)
    doc.setTextColor(20, 20, 20);
    doc.text(sigLabelText, sigRight, sigY + sigHeight + 6, { align: "right" });

    // Full DOLLUZ CORP logo watermark on every page (drawn last so it covers all
    // pages uniformly even if autoTable spawned extras). Small + faint so it
    // sits subtly behind the content (matches the reference payslip).
    addEagleWatermark(doc, {
        sizeRatio: 0.66,
        opacity: 0.14,
        topY: locationRowY != null ? locationRowY : undefined,
    });

    doc.save(filename);
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function EditModal({ payslip, onClose, onSaved }) {
    // payslip here is the LIST row (no items). Fetch the full detail with items.
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        lop_days: String(payslip.lop_days ?? 0),
        lop_deduction: "", // empty = auto-suggest from Basic; admin can override
        bonus: String(payslip.bonus ?? 0),
        other_earnings: String(payslip.other_earnings ?? 0),
        other_earnings_note: payslip.other_earnings_note ?? "",
        loan_deduction: String(payslip.loan_deduction ?? 0),
        other_deductions: String(payslip.other_deductions ?? 0),
        other_deductions_note: payslip.other_deductions_note ?? "",
        // Editable Company Address - gets overwritten with the snapshot from the
        // detail fetch below so the admin sees what's currently on the payslip.
        company_address: payslip.company_address ?? "",
        // Editable font preset - same snapshot model as company_address.
        font: payslip.font || DEFAULT_FONT_KEY,
    });
    const [saving, setSaving] = useState(false);
    const [attMsg, setAttMsg] = useState(null);   // attendance-refresh result banner

    // Editable structure items (same model as Salary Structure modal):
    //   { component_id, amount }
    // Initialised from the frozen payslip_item snapshots when detail loads.
    const [editEarnings, setEditEarnings] = useState(null);
    const [editDeductions, setEditDeductions] = useState(null);

    // Component options for the dropdowns
    const [earnOptions, setEarnOptions] = useState([]);
    const [dedOptions, setDedOptions] = useState([]);

    useEffect(() => {
        apiFetch("/api/payslip/components")
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setEarnOptions(list.filter((c) => c.component_type === "Earning"));
                setDedOptions(list.filter((c) => c.component_type === "Deduction"));
            })
            .catch((err) => console.error("❌ load components error:", err));
    }, []);

    useEffect(() => {
        apiFetch(`/api/payslip/management/detail/${payslip.payslip_id}`)
            .then(r => r.json())
            .then(d => {
                setDetail(d);
                // Pre-fill LOP override only if it differs from the auto-suggestion
                // (i.e. admin previously overrode it). Otherwise leave blank.
                const items = Array.isArray(d.items) ? d.items : [];
                const basicItem = items.find(i => i.component_type === "Earning" && i.component_name === "Basic");
                const basicAmt = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
                const lopDays = parseFloat(d.lop_days) || 0;
                const auto = lopDays > 0
                    ? parseFloat(((basicAmt / (d.days_in_month || 30)) * lopDays).toFixed(2))
                    : 0;
                const stored = parseFloat(d.lop_deduction) || 0;
                setForm(f => ({
                    ...f,
                    lop_deduction: Math.abs(stored - auto) > 0.01 ? String(stored) : "",
                    company_address: d.company_address ?? "",
                    font: d.font || DEFAULT_FONT_KEY,
                }));
                // Seed editable structure items from the frozen snapshots
                setEditEarnings(items.filter((i) => i.component_type === "Earning")
                    .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") })));
                setEditDeductions(items.filter((i) => i.component_type === "Deduction")
                    .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") })));
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [payslip.payslip_id]);

    // Editable-items helpers (mirror Payslip_Salary_Structure)
    const setRow = (type, idx, patch) => {
        const setter = type === "Earning" ? setEditEarnings : setEditDeductions;
        setter((rows) => (rows || []).map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };
    const addRow = (type) => {
        const setter = type === "Earning" ? setEditEarnings : setEditDeductions;
        setter((rows) => [...(rows || []), { component_id: "", amount: "" }]);
    };
    const removeRow = (type, idx) => {
        const setter = type === "Earning" ? setEditEarnings : setEditDeductions;
        setter((rows) => (rows || []).filter((_, i) => i !== idx));
    };
    const availableOptions = (allOptions, rows, currentIdx) => {
        const chosen = (rows || []).map((r, i) => (i === currentIdx ? null : r.component_id)).filter(Boolean);
        return allOptions.filter((o) => !chosen.includes(String(o.component_id)));
    };

    // Live calc uses the editable items when present
    const calc = recalc(
        detail || { ...payslip, items: [] },
        { ...form, editEarnings, editDeductions }
    );

    // Update LOP Days AND auto-fill LOP Deduction from the LIVE Basic value -
    // prefer the editable rows (because admin may have just changed Basic
    // inline) and fall back to the frozen snapshot.
    const setLopDays = (newVal) => {
        const days = parseFloat(newVal) || 0;
        const source = detail || payslip;
        const daysInMonth = source.days_in_month || 30;

        let basicAmt = 0;
        // Look up Basic via component options when editable rows exist
        if (Array.isArray(editEarnings) && earnOptions.length > 0) {
            const basicComp = earnOptions.find((c) => c.component_name === "Basic");
            if (basicComp) {
                const basicRow = editEarnings.find((r) => String(r.component_id) === String(basicComp.component_id));
                if (basicRow) basicAmt = parseFloat(basicRow.amount) || 0;
            }
        }
        if (!basicAmt) {
            const items = Array.isArray(source.items) ? source.items : [];
            const basicItem = items.find((i) => i.component_type === "Earning" && i.component_name === "Basic");
            basicAmt = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
        }

        const lopDed = days > 0 && basicAmt > 0
            ? parseFloat(((basicAmt / daysInMonth) * days).toFixed(2))
            : 0;
        setForm((f) => ({
            ...f,
            lop_days: newVal,
            lop_deduction: days > 0 ? String(lopDed) : "",
        }));
    };

    // Pull the leave-driven LOP for this employee/month from dTime attendance
    // (read-only) and drop it into the LOP Days field. Admin can still edit it.
    const refreshLopFromAttendance = async () => {
        setAttMsg(null);
        try {
            const src = detail || payslip;
            const qs = `emp_id=${encodeURIComponent(payslip.emp_id)}&year=${src.pay_year}&month=${src.pay_month}`;
            const res = await apiFetch(`/api/payslip/generate/attendance-lop?${qs}`);
            const d = await res.json();
            if (d.success && d.reconciles) {
                setLopDays(String(d.lop_days));
                setAttMsg({
                    ok: true,
                    text: `LOP set to ${d.lop_days} from attendance${d.unmarked_absent_days > 0 ? ` · ${d.unmarked_absent_days} unmarked-absent day(s) NOT included` : ""}.`,
                });
            } else {
                setAttMsg({ ok: false, text: d.message || "Attendance unavailable - enter LOP manually." });
            }
        } catch {
            setAttMsg({ ok: false, text: "Attendance unavailable - enter LOP manually." });
        }
    };

    const handleSave = async () => {
        const lopDays = parseFloat(form.lop_days) || 0;
        const lopDedEmpty = form.lop_deduction === "" || form.lop_deduction === null || form.lop_deduction === undefined;
        if (lopDays > 0 && lopDedEmpty) {
            dnAlert.warning("Missing LOP Deduction", `LOP Days is set to ${lopDays} but LOP Deduction is empty. Enter a deduction amount, or clear LOP Days if no deduction applies.`);
            return;
        }

        // Structure-item validations (same rules as Payslip_Salary_Structure)
        const earnRows = (editEarnings || []).filter((r) => r.component_id);
        const dedRows = (editDeductions || []).filter((r) => r.component_id);
        const showErr = (msg) => dnAlert.warning("Invalid structure", msg.replace(/<[^>]+>/g, ""));

        if (earnRows.length === 0) {
            showErr("Add at least one Earning component in the structure table.");
            return;
        }
        if (earnRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)))) {
            showErr("Every Earning component must have an amount (0 or more).");
            return;
        }
        if (dedRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)))) {
            showErr("Every Deduction component must have an amount (0 or more).");
            return;
        }
        if ([...earnRows, ...dedRows].some((r) => {
            const n = parseFloat(r.amount);
            return !isNaN(n) && n > MAX_AMOUNT_PM;
        })) {
            showErr(`Amount(s) exceed the maximum of ₹${MAX_AMOUNT_PM.toLocaleString("en-IN")} per item.`);
            return;
        }
        if (calc.netPay <= 0) {
            showErr(`Net Pay is <strong>₹${fmt(calc.netPay)}</strong>. Earnings must exceed total deductions - review the structure or adjustments.`);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                lop_days: parseFloat(form.lop_days) || 0,
                lop_deduction: form.lop_deduction === "" ? "" : (parseFloat(form.lop_deduction) || 0),
                bonus: parseFloat(form.bonus) || 0,
                other_earnings: parseFloat(form.other_earnings) || 0,
                other_earnings_note: form.other_earnings_note || null,
                loan_deduction: parseFloat(form.loan_deduction) || 0,
                other_deductions: parseFloat(form.other_deductions) || 0,
                other_deductions_note: form.other_deductions_note || null,
                company_address: (form.company_address || "").trim() || null,
                font: form.font || DEFAULT_FONT_KEY,
                // Per-payslip overridden items - backend will delete existing
                // payslip_items and re-insert these as the new snapshot.
                items: [
                    ...earnRows.map((r) => ({ component_id: parseInt(r.component_id), amount: parseFloat(r.amount), component_type: "Earning" })),
                    ...dedRows.map((r) => ({ component_id: parseInt(r.component_id), amount: parseFloat(r.amount), component_type: "Deduction" })),
                ],
            };

            const res = await apiFetch(
                `/api/payslip/management/update/${payslip.payslip_id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Saved!", "Payslip updated successfully.");
                onSaved();
            } else {
                dnAlert.error("Error", data.error || "Could not update payslip.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const field = (label, key, isNote = false, extra = null) => (
        <div className="pm-ef-field" key={key}>
            <label>{label}{extra}</label>
            <input
                type={isNote ? "text" : "number"}
                min={isNote ? undefined : "0"}
                step={key === "lop_days" ? "0.5" : "1"}
                className="pm-ef-input"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={isNote ? "Optional note…" : (key === "lop_deduction" ? "Auto (leave blank)" : "0")}
                {...(isNote ? {} : numericInputProps)}
            />
        </div>
    );

    return (
        <>
            <div className="pm-modal-overlay" />
            <div className="pm-modal pm-edit-modal">
                <div className="pm-modal-header">
                    <div>
                        <div className="pm-modal-title">Edit payslip</div>
                        <div className="pm-modal-sub">
                            {payslip.emp_first_name} {payslip.emp_last_name} &nbsp;·&nbsp; {payslip.payslip_number}
                        </div>
                    </div>
                    <button className="pm-modal-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="pm-modal-body">
                    {loading ? (
                        <div className="pm-loading">Loading payslip…</div>
                    ) : (
                        <>
                            <div className="pm-ef-section-label">Adjustments</div>
                            {/* Two-section layout mirroring Payslip_Generate Step 3:
                                Earnings (left) and Deductions (right). LOP Days +
                                LOP Deduction sit inside a grouped slate box because
                                the second auto-derives from the first. */}
                            <div className="pm-ef-sections">
                                {/* ── EARNINGS column ── */}
                                <div className="pm-ef-section pm-ef-section--earn">
                                    <div className="pm-ef-section-head">
                                        <span>Earnings</span>
                                        <span className="pm-ef-section-sub">one-off additions</span>
                                    </div>
                                    <div className="pm-ef-section-body">
                                        {field("Bonus (₹)", "bonus")}
                                        {field("Other Earnings (₹)", "other_earnings")}
                                        {field("Other Earnings Note", "other_earnings_note", true)}
                                    </div>
                                </div>

                                {/* ── DEDUCTIONS column ── */}
                                <div className="pm-ef-section pm-ef-section--ded">
                                    <div className="pm-ef-section-head">
                                        <span>Deductions</span>
                                        <span className="pm-ef-section-sub">LOP & one-off recoveries</span>
                                    </div>
                                    <div className="pm-ef-section-body">
                                        <div className="pm-ef-row-pair">
                                            <div className="pm-ef-field" key="lop_days">
                                                <label>LOP Days</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    className="pm-ef-input"
                                                    value={form.lop_days}
                                                    onChange={(e) => setLopDays(e.target.value)}
                                                    placeholder="0"
                                                    {...numericInputProps}
                                                />
                                            </div>
                                            <div className="pm-ef-field" key="lop_deduction">
                                                <label>LOP Deduction (₹)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="pm-ef-input"
                                                    value={form.lop_deduction}
                                                    onChange={(e) => setForm((f) => ({ ...f, lop_deduction: e.target.value }))}
                                                    placeholder="0"
                                                    {...numericInputProps}
                                                />
                                            </div>
                                        </div>
                                        <div className="pm-ef-att">
                                            <button type="button" className="pm-ef-att-btn" onClick={refreshLopFromAttendance}>
                                                ↻ Refresh LOP from attendance
                                            </button>
                                            {attMsg && (
                                                <span className={`pm-ef-att-msg ${attMsg.ok ? "ok" : "warn"}`}>{attMsg.text}</span>
                                            )}
                                        </div>
                                        {field("Loan / Advance Recovery (₹)", "loan_deduction")}
                                        {field("Other Deductions (₹)", "other_deductions")}
                                        {field("Other Deductions Note", "other_deductions_note", true)}
                                    </div>
                                </div>
                            </div>

                            {/* Editable 4-column structure table - same shape as
                                Payslip_Salary_Structure / Payslip_Generate Step 3.
                                Admin can edit Basic/HRA/PF/etc., add/remove components
                                per-payslip. One-off adjustments from the section above
                                (Bonus/LOP/Loan/Other) appear as read-only addendum rows
                                at the bottom with the typed note text as label. */}
                            <div className="pm-ef-section-label" style={{ marginTop: 20 }}>Live Preview</div>
                            {(() => {
                                const earnList = editEarnings || [];
                                const dedList = editDeductions || [];
                                const addEarn = [
                                    ...(calc.bonus > 0 ? [{ label: "Bonus", amount: calc.bonus }] : []),
                                    ...(calc.otherEarn > 0 ? [{ label: (form.other_earnings_note || "").trim() || "Other Earnings", amount: calc.otherEarn }] : []),
                                ];
                                const addDed = [
                                    ...(calc.lopDed > 0 ? [{ label: "LOP deduction", amount: calc.lopDed }] : []),
                                    ...(calc.loanDed > 0 ? [{ label: "Loan / advance", amount: calc.loanDed }] : []),
                                    ...(calc.otherDed > 0 ? [{ label: (form.other_deductions_note || "").trim() || "Other Deductions", amount: calc.otherDed }] : []),
                                ];
                                const maxStruct = Math.max(earnList.length, dedList.length, 1);
                                const maxAddon = Math.max(addEarn.length, addDed.length);

                                const renderEditCell = (type, rows, idx) => {
                                    const row = rows[idx];
                                    const opts = type === "Earning" ? earnOptions : dedOptions;
                                    if (!row) {
                                        return (<><div className="pm-pay-cell" /><div className="pm-pay-cell" /></>);
                                    }
                                    const available = availableOptions(opts, rows, idx);
                                    const compOpts = available.map((o) => ({ value: String(o.component_id), label: o.component_name }));
                                    if (row.component_id && !compOpts.find((o) => o.value === String(row.component_id))) {
                                        const c = opts.find((o) => String(o.component_id) === String(row.component_id));
                                        if (c) compOpts.unshift({ value: String(c.component_id), label: c.component_name });
                                    }
                                    const current = compOpts.find((o) => o.value === String(row.component_id)) || null;
                                    return (
                                        <>
                                            <div className="pm-pay-cell pm-pay-cell-select">
                                                <div className="pm-pay-rs-wrap">
                                                    <Select
                                                        classNamePrefix="pm-rs"
                                                        options={compOpts}
                                                        value={current}
                                                        isSearchable
                                                        isClearable
                                                        placeholder="- select -"
                                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                        menuPlacement="auto"
                                                        maxMenuHeight={220}
                                                        styles={{ menuPortal: (b) => ({ ...b, zIndex: 99999 }) }}
                                                        onChange={(opt) => setRow(type, idx, { component_id: opt ? opt.value : "" })}
                                                    />
                                                </div>
                                                <button
                                                    className="pm-pay-remove"
                                                    title="Remove"
                                                    onClick={() => removeRow(type, idx)}
                                                ><FaTrash /></button>
                                            </div>
                                            <div className="pm-pay-cell pm-pay-cell-amount">
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    min="0"
                                                    value={row.amount}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => {
                                                        if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                                    }}
                                                    onPaste={(e) => {
                                                        const txt = (e.clipboardData || window.clipboardData).getData("text");
                                                        if (!/^\d*\.?\d*$/.test(txt)) e.preventDefault();
                                                    }}
                                                    onChange={(e) => {
                                                        let v = e.target.value;
                                                        if (v) {
                                                            v = v.replace(/^0+(?=[1-9])/, "");
                                                            const m = v.match(/^(\d*)(?:\.(\d{0,2}))?/);
                                                            if (m) v = m[1] + (m[2] !== undefined ? "." + m[2] : "");
                                                        }
                                                        setRow(type, idx, { amount: v });
                                                    }}
                                                />
                                            </div>
                                        </>
                                    );
                                };

                                return (
                                    <div className={`pm-pay-table ${calc.netPay < 0 ? "pm-pay-table--neg" : ""}`} style={fontCssStyle(form.font)}>
                                        <div className="pm-pay-row pm-pay-head">
                                            <div className="pm-pay-cell">Earnings</div>
                                            <div className="pm-pay-cell pm-pay-right">Monthly in INR</div>
                                            <div className="pm-pay-cell">Deductions</div>
                                            <div className="pm-pay-cell pm-pay-right">Monthly in INR</div>
                                        </div>
                                        {Array.from({ length: maxStruct }).map((_, i) => (
                                            <div className="pm-pay-row" key={`pmrow-${i}`}>
                                                {renderEditCell("Earning", earnList, i)}
                                                {renderEditCell("Deduction", dedList, i)}
                                            </div>
                                        ))}
                                        <div className="pm-pay-row pm-pay-addrow">
                                            <div className="pm-pay-cell pm-pay-cell-add">
                                                <button onClick={() => addRow("Earning")}><FaPlus /> Add earning</button>
                                            </div>
                                            <div className="pm-pay-cell" />
                                            <div className="pm-pay-cell pm-pay-cell-add">
                                                <button onClick={() => addRow("Deduction")}><FaPlus /> Add deduction</button>
                                            </div>
                                            <div className="pm-pay-cell" />
                                        </div>
                                        {maxAddon > 0 && Array.from({ length: maxAddon }).map((_, i) => {
                                            const e = addEarn[i];
                                            const d = addDed[i];
                                            return (
                                                <div className="pm-pay-row pm-pay-addon" key={`pmaddon-${i}`}>
                                                    <div className="pm-pay-cell">{e ? e.label : ""}</div>
                                                    <div className="pm-pay-cell pm-pay-right">{e ? fmt(e.amount) : ""}</div>
                                                    <div className="pm-pay-cell">{d ? d.label : ""}</div>
                                                    <div className="pm-pay-cell pm-pay-right">{d ? fmt(d.amount) : ""}</div>
                                                </div>
                                            );
                                        })}
                                        <div className="pm-pay-row pm-pay-totals">
                                            <div className="pm-pay-cell">Total Earnings</div>
                                            <div className="pm-pay-cell pm-pay-right">{fmt(calc.gross)}</div>
                                            <div className="pm-pay-cell">Total Deductions</div>
                                            <div className="pm-pay-cell pm-pay-right">{fmt(calc.totalDed)}</div>
                                        </div>
                                        <div className="pm-pay-row pm-pay-netpay">
                                            <div className="pm-pay-cell" />
                                            <div className="pm-pay-cell" />
                                            <div className="pm-pay-cell">NET PAY</div>
                                            <div className="pm-pay-cell pm-pay-right pm-pay-netpay-val">₹{fmt(calc.netPay)}</div>
                                        </div>
                                    </div>
                                );
                            })()}
                            {calc.netPay < 0 && (
                                <div className="pm-ef-warn">⚠ Net pay is negative - review adjustments before saving.</div>
                            )}

                            {/* Footer - Company Address override only. The static Note
                                + Authorised Signatory line are rendered on the PDF as
                                hard-coded copy; no need to surface them here. */}
                            <div className="pm-ef-section-label" style={{ marginTop: 20 }}>Footer</div>
                            <div className="pm-ef-footer">
                                <div className="pm-ef-footer-address pm-ef-footer-address--full">
                                    <label>Company Address</label>
                                    <textarea
                                        rows={3}
                                        value={form.company_address}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, company_address: e.target.value }))
                                        }
                                        placeholder="e.g. DOLLUZ CORP, Floor 3, Tech Park, Chennai 600001"
                                    />
                                </div>
                                {/* Per-payslip font override - applies to the PDF +
                                    HTML preview modal next time it's opened. */}
                                <div className="pm-ef-footer-font">
                                    <label>Payslip Font</label>
                                    <select
                                        value={form.font || DEFAULT_FONT_KEY}
                                        onChange={(e) => setForm((f) => ({ ...f, font: e.target.value }))}
                                        style={fontCssStyle(form.font)}
                                    >
                                        {PAYSLIP_FONTS.map((f) => (
                                            <option key={f.key} value={f.key} style={fontCssStyle(f.key)}>
                                                {f.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="pm-modal-footer">
                    <button className="pm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="pm-btn-save" onClick={handleSave} disabled={saving || loading}>
                        {saving ? "Saving…" : "Save Changes"}
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

// PreviewModal - renders the EXACT same layout as the downloadable PDF via the
// shared PayslipPreviewBody component. Anything that changes in the payslip
// layout lives in PayslipPreviewBody.js (and must be mirrored in
// generatePayslipPDF() above).
function PreviewModal({ payslipId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiFetch(`/api/payslip/management/detail/${payslipId}`)
            .then(r => r.json())
            .then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, [payslipId]);

    return (
        <>
            <div className="pm-modal-overlay" />
            <div className="pm-modal pm-preview-modal">
                <div className="pm-modal-header">
                    <div>
                        <div className="pm-modal-title">Payslip preview</div>
                        {data && (
                            <div className="pm-modal-sub">
                                {data.payslip_number} &nbsp;·&nbsp; {MONTH_NAMES[data.pay_month]} {data.pay_year}
                            </div>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {data && (
                            <button className="pm-btn-pdf" onClick={() => generatePayslipPDF(data)}>
                                <FaFilePdf style={{ marginRight: 5 }} /> Download PDF
                            </button>
                        )}
                        <button className="pm-modal-close" onClick={onClose}><FaTimes /></button>
                    </div>
                </div>

                <div className="pm-modal-body pm-prev-pdf-body">
                    {loading ? (
                        <div className="pm-loading">Loading payslip…</div>
                    ) : !data ? (
                        <div className="pm-empty">Could not load payslip details.</div>
                    ) : (
                        <PayslipPreviewBody data={data} />
                    )}
                </div>
            </div>
        </>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function Payslip_Management({ navSize }) {
    const navigate = useNavigate();

    // Month-range state. Default: full current calendar year (Jan - Dec) so the
    // admin sees the whole year at a glance and can narrow down if needed.
    const currentCalendarYear = now.getFullYear();
    const [fromYear, setFromYear] = useState(currentCalendarYear);
    const [fromMonth, setFromMonth] = useState(1);
    const [toYear, setToYear] = useState(currentCalendarYear);
    const [toMonth, setToMonth] = useState(12);
    const [loaded, setLoaded] = useState(false);

    const [payslips, setPayslips] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    // Snapshot of the range that was last loaded - used for labels so editing the
    // pickers without re-clicking Load doesn't make the page lie about its data.
    const [loadedRange, setLoadedRange] = useState(null);
    const [empFilter, setEmpFilter] = useState("");           // dropdown selection (pending)
    const [appliedEmpFilter, setAppliedEmpFilter] = useState("");   // applied only on Load click

    // Modals
    const [previewId, setPreviewId] = useState(null);
    const [editPayslip, setEditPayslip] = useState(null);

    const [publishing, setPublishing] = useState(false); // publish all / publish selected

    // ── Bulk actions ──
    // Selection lives INSIDE each action's modal, not in the table. Publish All
    // and Remind All both open a picker listing everyone the action would touch,
    // pre-ticked, so the default is "all of them" and narrowing is a matter of
    // unticking. That removes the per-row checkbox column entirely and means the
    // list you confirm against is exactly the list that gets acted on.
    const [publishPicker, setPublishPicker] = useState(null);
    // { rows: [payslip], checked: [payslip_id] }
    // Filters inside the pickers. Shared shape so both modals use one set of
    // helpers: narrow the visible list without disturbing what's ticked.
    const EMPTY_PICK_FILTERS = { emp_id: "", fromMonth: 1, fromYear: 0, toMonth: 12, toYear: 0 };
    const [pickFilters, setPickFilters] = useState(EMPTY_PICK_FILTERS);
    const [mailFilters, setMailFilters] = useState(EMPTY_PICK_FILTERS);
    const [downloadHistory, setDownloadHistory] = useState(null); // { payslip, rows, loading }
    const [emailModal, setEmailModal] = useState(null); // { ids, preview, loading, sending, result }
    const [emailHistory, setEmailHistory] = useState(null); // { payslip, rows, loading }
    // Full send log across every payslip - opened from the toolbar, filterable.
    // null = closed. Filters live in here so reopening starts clean.
    const [mailLog, setMailLog] = useState(null);
    // { open, loading, rows, total, employees, filters: { from/to period, emp_id, q } }

    // Results status filter: all | draft | published | emailed | not_emailed
    const [statusFilter, setStatusFilter] = useState("all");

    // ── Auth ──
    useEffect(() => {
        apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" })
            .then(r => r.json())
            .then(data => {
                if (
                    data?.message === "Access Denied. No Token Provided!" ||
                    data?.message === "Invalid Token"
                ) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            })
            .catch(() => navigate("/login"));
    }, [navigate]);

    // ── Validate range before fetching ──
    const rangeInverted = cmpYM(toYear, toMonth, fromYear, fromMonth) < 0;

    // ── Fetch data ──
    const fetchData = useCallback(async () => {
        if (cmpYM(toYear, toMonth, fromYear, fromMonth) < 0) {
            dnAlert.warning("Invalid range", "End month cannot be before start month.");
            return;
        }
        setLoading(true);
        try {
            const qs = `from_year=${fromYear}&from_month=${fromMonth}&to_year=${toYear}&to_month=${toMonth}`;
            const [listRes, summRes] = await Promise.all([
                apiFetch(`/api/payslip/management/list?${qs}`),
                apiFetch(`/api/payslip/management/summary?${qs}`),
            ]);
            const listData = await listRes.json();
            const summData = await summRes.json();
            setPayslips(Array.isArray(listData) ? listData : []);
            setSummary(summData);
            setLoadedRange({ fromYear, fromMonth, toYear, toMonth });
            setLoaded(true);
        } catch (err) {
            console.error("❌ fetchData error:", err);
        } finally {
            setLoading(false);
        }
    }, [fromYear, fromMonth, toYear, toMonth]);

    const handleLoad = () => { setAppliedEmpFilter(empFilter); fetchData(); };

    // Auto-load on first mount so admin sees current-year data right away.
    // The default From/To is Jan-Dec of the current year, so this gives a
    // sensible starting view without making the admin click "Load Payslips".
    // We intentionally fire this only once (empty deps); subsequent loads go
    // through handleLoad after the admin tweaks the range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, []);

    // ── Group payslips by ${pay_year}-${pay_month}; preserve summary.months order
    //    so empty months in the range still render their section header. ─────
    // Employee options for the filter, derived from the loaded payslips.
    const empFilterOptions = useMemo(() => {
        const seen = new Map();
        payslips.forEach(p => {
            if (!seen.has(p.emp_id)) seen.set(p.emp_id, `${p.emp_first_name || ""} ${p.emp_last_name || ""}`.trim() || p.emp_id);
        });
        const list = [...seen].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ value: id, label: `${name} (${id})` }));
        return [{ value: "", label: "All employees" }, ...list];
    }, [payslips]);

    // Grouped by month; also filtered by the selected employee AND status so the
    // table AND both exports (which iterate this) show only the chosen subset.
    const groupedPayslips = useMemo(() => {
        let src = appliedEmpFilter ? payslips.filter(p => p.emp_id === appliedEmpFilter) : payslips;
        if (statusFilter === "draft") src = src.filter(p => p.status === "Draft");
        else if (statusFilter === "published") src = src.filter(p => p.status === "Published");
        else if (statusFilter === "emailed") src = src.filter(p => p.emailed);
        else if (statusFilter === "not_emailed") src = src.filter(p => p.status === "Published" && !p.emailed);
        const map = {};
        src.forEach(p => {
            const key = `${p.pay_year}-${p.pay_month}`;
            (map[key] ||= []).push(p);
        });
        const monthOrder = summary?.months || [];
        return monthOrder
            .map(m => ({
                pay_year: m.pay_year,
                pay_month: m.pay_month,
                summary: m,
                rows: map[`${m.pay_year}-${m.pay_month}`] || [],
            }))
            // When a status filter is active, hide months that have no matching
            // rows so the list isn't padded with empty month bands.
            .filter(g => statusFilter === "all" || g.rows.length > 0);
    }, [payslips, summary, appliedEmpFilter, statusFilter]);

    // ── Publish single ──
    // Routed through runStrictPublish too, so a blocked previous month renders
    // the same detailed table here as it does for the two bulk actions instead
    // of a bare error toast.
    const handlePublishOne = async (payslip) => {
        const ok = await dnAlert.confirm({
            title: "Publish Payslip?",
            message: `Publish ${payslip.emp_first_name} ${payslip.emp_last_name}'s payslip for ${MONTH_NAMES[payslip.pay_month]} ${payslip.pay_year}? Employees will be able to download it immediately.`,
            confirmLabel: "Yes, Publish",
        });
        if (!ok) return;
        await runStrictPublish({
            url: `/api/payslip/management/publish_selected`,
            body: { payslip_ids: [payslip.payslip_id] },
            successTitle: "Published!",
            blockedTitle: "Cannot Publish Payslip",
        });
    };

    // Does a row survive a picker's filters? Rows carry pay_month / pay_year /
    // emp_id in both pickers, so one predicate serves both.
    const passesPickFilter = (row, f) => {
        if (f.emp_id && row.emp_id !== f.emp_id) return false;
        if (cmpYM(row.pay_year, row.pay_month, f.fromYear, f.fromMonth) < 0) return false;
        if (cmpYM(row.pay_year, row.pay_month, f.toYear, f.toMonth) > 0) return false;
        return true;
    };
    // Employee options built from whatever rows a picker is showing, so the
    // dropdown never offers someone the list can't produce.
    const pickEmpOptions = (rows) => {
        const seen = new Map();
        rows.forEach(r => {
            if (!seen.has(r.emp_id)) {
                seen.set(r.emp_id, r.employee_name ||
                    `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim() || r.emp_id);
            }
        });
        return [
            { value: "", label: `All employees (${seen.size})` },
            ...[...seen].sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => ({ value: id, label: `${name} (${id})` })),
        ];
    };

    // ── Publish picker (opened by Publish All) ──
    const openPublishPicker = () => {
        const rows = payslips
            .filter(p => p.status === "Draft")
            .sort((a, b) =>
                a.pay_year - b.pay_year || a.pay_month - b.pay_month ||
                `${a.emp_first_name} ${a.emp_last_name}`.localeCompare(`${b.emp_first_name} ${b.emp_last_name}`));
        if (rows.length === 0) {
            dnAlert.info("Nothing to publish", `All payslips for ${labelForLoaded} are already published.`);
            return;
        }
        const r = loadedRange || { fromYear, fromMonth, toYear, toMonth };
        setPickFilters({ emp_id: "", fromMonth: r.fromMonth, fromYear: r.fromYear, toMonth: r.toMonth, toYear: r.toYear });
        // Everything ticked by default - the button says "All", so that is the
        // starting position; unticking is how you narrow it.
        setPublishPicker({ rows, checked: rows.map(r2 => r2.payslip_id) });
    };
    const togglePublishPick = (id) =>
        setPublishPicker(p => ({
            ...p,
            checked: p.checked.includes(id) ? p.checked.filter(x => x !== id) : [...p.checked, id],
        }));
    // Select-all acts on what the filter is SHOWING. Rows hidden by a filter
    // keep whatever state they had - the footer surfaces that separately so a
    // narrowed view can never silently publish someone off-screen.
    const togglePublishPickAll = (visible) =>
        setPublishPicker(p => {
            const ids = visible.map(r => r.payslip_id);
            const allOn = ids.length > 0 && ids.every(id => p.checked.includes(id));
            return {
                ...p,
                checked: allOn
                    ? p.checked.filter(id => !ids.includes(id))
                    : [...new Set([...p.checked, ...ids])],
            };
        });

    // Open the preview + send modal for the given payslip ids.
    //
    // Every recipient is previewable, not just the first. We fetch a cheap
    // roster up front (one row per recipient, no HTML) and then render each
    // employee's actual email on demand as the admin pages through, caching
    // what has been fetched. That way a 50-person send costs one roster call
    // plus one render per email actually looked at, instead of 50 renders
    // nobody asked for.
    // opts.previewOnly - opened from the row's "email preview" button: show
    // exactly what the employee would receive, with no Send in the footer.
    // Sending from a row is a separate button (handleSendOne).
    const openEmailModal = async (ids, opts = {}) => {
        if (!ids || ids.length === 0) return;
        setEmailModal({
            ids, recipients: [], index: 0, previews: {},
            previewOnly: !!opts.previewOnly,
            loading: true, sending: false, result: null,
        });
        try {
            const res = await apiFetch("/api/payslip/management/email/recipients", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ payslip_ids: ids }),
            });
            const data = await res.json();
            if (!res.ok) {
                setEmailModal(m => ({ ...m, loading: false, error: data.error || "Could not build preview." }));
                return;
            }
            const recipients = data.recipients || [];
            const rr = loadedRange || { fromYear, fromMonth, toYear, toMonth };
            setMailFilters({ emp_id: "", fromMonth: rr.fromMonth, fromYear: rr.fromYear, toMonth: rr.toMonth, toYear: rr.toYear });
            setEmailModal(m => ({
                ...m,
                recipients,
                // Everyone who CAN be mailed starts ticked; unticking is how the
                // admin narrows a reminder run. Rows that would be skipped
                // anyway (draft, or no address on file) are never ticked and
                // cannot be.
                checked: recipients.filter(r => r.sendable).map(r => r.payslip_id),
                noEmailCount: data.no_email_count || 0,
                notPublishedCount: data.not_published_count || 0,
                loading: false,
            }));
            if (recipients.length > 0) loadPreviewAt(0, recipients);
        } catch {
            setEmailModal(m => ({ ...m, loading: false, error: "Network error." }));
        }
    };

    // Render (and cache) one recipient's email. Safe to call repeatedly - a
    // cached index short-circuits.
    const loadPreviewAt = async (index, recipientList) => {
        const list = recipientList || emailModal?.recipients || [];
        const r = list[index];
        if (!r) return;
        setEmailModal(m => (m ? { ...m, index } : m));
        // Already rendered? nothing to do.
        let cached = false;
        setEmailModal(m => { cached = !!m?.previews?.[r.payslip_id]; return m; });
        if (cached) return;

        setEmailModal(m => (m ? { ...m, previewLoading: true } : m));
        try {
            const res = await apiFetch("/api/payslip/management/email/preview", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ payslip_id: r.payslip_id }),
            });
            const data = await res.json();
            setEmailModal(m => m ? {
                ...m,
                previewLoading: false,
                previews: { ...m.previews, [r.payslip_id]: res.ok ? data : { error: data.error || "Could not build preview." } },
            } : m);
        } catch {
            setEmailModal(m => m ? {
                ...m,
                previewLoading: false,
                previews: { ...m.previews, [r.payslip_id]: { error: "Network error." } },
            } : m);
        }
    };

    const stepPreview = (delta) => {
        if (!emailModal?.recipients?.length) return;
        const next = emailModal.index + delta;
        if (next < 0 || next >= emailModal.recipients.length) return;
        loadPreviewAt(next);
    };

    // Recipient tick helpers. Only `sendable` rows can ever be ticked.
    const toggleRecipient = (id) =>
        setEmailModal(m => ({
            ...m,
            checked: m.checked.includes(id) ? m.checked.filter(x => x !== id) : [...m.checked, id],
        }));
    // Same rule as the publish picker: select-all applies to the filtered view.
    const toggleAllRecipients = (visible) =>
        setEmailModal(m => {
            const ids = visible.filter(r => r.sendable).map(r => r.payslip_id);
            const allOn = ids.length > 0 && ids.every(id => m.checked.includes(id));
            return {
                ...m,
                checked: allOn
                    ? m.checked.filter(id => !ids.includes(id))
                    : [...new Set([...m.checked, ...ids])],
            };
        });

    // ── Send one payslip email straight from its row ──
    // Deliberately does NOT open the preview modal - the row has a separate
    // preview button for that. Confirms first, since this dispatches for real.
    const handleSendOne = async (payslip) => {
        const who = `${payslip.emp_first_name || ""} ${payslip.emp_last_name || ""}`.trim() || payslip.emp_id;
        const ok = await dnAlert.confirm({
            title: "Send Payslip Email?",
            message: `Email ${who} their ${MONTH_NAMES[payslip.pay_month]} ${payslip.pay_year} payslip now?`,
            confirmLabel: "Send Email",
        });
        if (!ok) return;
        try {
            const res = await apiFetch("/api/payslip/management/email/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ payslip_ids: [payslip.payslip_id] }),
            });
            const data = await res.json();
            if (!res.ok) {
                dnAlert.error("Send failed", data.error || "Could not send the email.");
                return;
            }
            if (data.sent > 0) dnAlert.success("Email sent", `Payslip email sent to ${who}.`);
            else dnAlert.warning("Nothing sent", "The payslip was skipped - check the employee has an email on file.");
            fetchData();
        } catch {
            dnAlert.error("Error", "Network error.");
        }
    };

    // ── Download history for one payslip ──
    const openDownloadHistory = async (payslip) => {
        setDownloadHistory({ payslip, rows: [], loading: true });
        try {
            const res = await apiFetch(`/api/payslip/management/download/history/${payslip.payslip_id}`);
            const data = await res.json();
            setDownloadHistory({ payslip, rows: Array.isArray(data) ? data : [], loading: false });
        } catch {
            setDownloadHistory({ payslip, rows: [], loading: false });
        }
    };

    // ── Full email log (toolbar → Email History) ──
    // Filters are sent to the server rather than applied client-side, so the
    // log stays usable once it has thousands of rows. Scoped to the loaded pay
    // period by default, matching what the table below is showing.
    const fetchMailLog = useCallback(async (filters) => {
        const qs = new URLSearchParams({
            from_year: filters.fromYear, from_month: filters.fromMonth,
            to_year: filters.toYear, to_month: filters.toMonth,
        });
        if (filters.emp_id) qs.set("emp_id", filters.emp_id);
        if (filters.q) qs.set("q", filters.q);

        setMailLog(m => ({ ...(m || {}), filters, loading: true }));
        try {
            const res = await apiFetch(`/api/payslip/management/email/log?${qs}`);
            const data = await res.json();
            setMailLog(m => ({
                ...(m || {}), filters, loading: false,
                rows: data.rows || [], total: data.total || 0,
                employees: data.employees || [],
            }));
        } catch {
            setMailLog(m => ({ ...(m || {}), filters, loading: false, rows: [], total: 0, error: "Could not load the email log." }));
        }
    }, []);

    // The log opens on the range the page is currently showing, but then owns
    // its own From/To - narrowing the history must not disturb the table
    // underneath, and widening it must not require reloading the page first.
    const openMailLog = () => {
        const r = loadedRange || { fromYear, fromMonth, toYear, toMonth };
        const filters = {
            fromYear: r.fromYear, fromMonth: r.fromMonth,
            toYear: r.toYear, toMonth: r.toMonth,
            emp_id: "", q: "",
        };
        setMailLog({ loading: true, rows: [], total: 0, employees: [], filters });
        fetchMailLog(filters);
    };
    const setMailLogFilter = (patch) => {
        const next = { ...mailLog.filters, ...patch };
        // Guard the same inverted-range case the main filter bar guards.
        if (cmpYM(next.toYear, next.toMonth, next.fromYear, next.fromMonth) < 0) {
            dnAlert.warning("Invalid range", "End month cannot be before start month.");
            return;
        }
        fetchMailLog(next);
    };
    const resetMailLogFilters = () => {
        const f = mailLog.filters;
        fetchMailLog({ ...f, emp_id: "", q: "" });
    };

    // Open the per-payslip send-history modal.
    const openEmailHistory = async (payslip) => {
        setEmailHistory({ payslip, rows: [], loading: true });
        try {
            const res = await apiFetch(`/api/payslip/management/email/history/${payslip.payslip_id}`);
            const data = await res.json();
            setEmailHistory({ payslip, rows: Array.isArray(data) ? data : [], loading: false });
        } catch {
            setEmailHistory({ payslip, rows: [], loading: false });
        }
    };

    // Fire the send for every id in the open modal.
    const handleSendEmails = async () => {
        // Send exactly what is ticked, not everything the modal was opened with.
        const toSend = emailModal?.checked || [];
        if (!emailModal || toSend.length === 0) return;
        setEmailModal((m) => ({ ...m, sending: true }));
        try {
            const res = await apiFetch("/api/payslip/management/email/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ payslip_ids: toSend }),
            });
            const data = await res.json();
            if (!res.ok) {
                setEmailModal((m) => ({ ...m, sending: false, result: { error: data.error || "Send failed." } }));
                return;
            }
            setEmailModal((m) => ({ ...m, sending: false, result: data }));
            if (data.sent > 0) {
                dnAlert.success("Emails sent", `${data.sent} payslip email${data.sent !== 1 ? "s" : ""} sent.`);
            }
            // Re-read the list so the Emailed column, the Emailed / Not-emailed
            // chips and the per-month counts reflect the send. Without this the
            // row still read "No mails sent" until a manual reload.
            fetchData();
        } catch {
            setEmailModal((m) => ({ ...m, sending: false, result: { error: "Network error." } }));
        }
    };

    // ── Build the blocked-employees table for the dnAlert report modal ──
    // (includes a Month column since blocked rows can span multiple months)
    // Every interpolated value is DB-sourced, so escape before it reaches
    // dangerouslySetInnerHTML.
    const buildBlockedTableHtml = (data) => {
        const rows = data.blocked.map((b, i) => {
            const bg = i % 2 === 0 ? "#ffffff" : "#fdf6f6";
            return `
                <tr style="background:${bg};border-bottom:1px solid #f0e0e0;">
                    <td style="padding:8px 10px;font-weight:500;">${escapeHtml(b.emp_name)}</td>
                    <td style="padding:8px 10px;color:#555;font-size:12px;">${escapeHtml(b.emp_id)}</td>
                    <td style="padding:8px 10px;font-size:12px;">${escapeHtml(b.month_label || "")}</td>
                    <td style="padding:8px 10px;font-size:12px;">${escapeHtml(b.prev_payslip_number)}</td>
                    <td style="padding:8px 10px;">
                        <span style="display:inline-block;background:#fde8e8;color:#c0392b;border:1px solid #f5c6cb;border-radius:4px;padding:2px 7px;font-size:11px;white-space:nowrap;">
                            ${escapeHtml(b.prev_month_label || data.prev_month_label)} still ${escapeHtml(b.prev_status)}
                        </span>
                    </td>
                </tr>`;
        }).join("");

        return `
            <div style="margin-top:6px;font-size:13px;color:#555;">
                ${escapeHtml(data.error)} Publish the pending previous-month payslips
                first, then retry${data.attempted_count
                ? ` - none of the ${data.attempted_count} selected payslips were published.`
                : "."}
            </div>
            <div style="overflow-x:auto;overflow-y:auto;max-height:340px;margin-top:12px;border:1px solid #e0c8c8;border-radius:8px;">
                <table style="width:100%;border-collapse:collapse;font-size:13px;font-family:Segoe UI,sans-serif;">
                    <thead>
                        <tr style="background:#c0392b;color:#fff;position:sticky;top:0;z-index:1;">
                            <th style="padding:10px 12px;text-align:left;">Employee</th>
                            <th style="padding:10px 12px;text-align:left;">Emp ID</th>
                            <th style="padding:10px 12px;text-align:left;">Blocked Month</th>
                            <th style="padding:10px 12px;text-align:left;">Pending Payslip</th>
                            <th style="padding:10px 12px;text-align:left;">Issue</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>`;
    };

    // ── Shared strict publish runner ──
    // Every publish path (single, selective, all) funnels through here so they
    // behave identically. The backend validates the WHOLE set before writing
    // anything: if any row's previous month is unpublished, nothing publishes
    // and we show the complete blocker list. There is deliberately no
    // "publish the rest anyway" option - a partial publish cannot be undone
    // because published payslips are permanent.
    const runStrictPublish = async ({ url, body, successTitle, blockedTitle }) => {
        setPublishing(true);
        try {
            const res = await apiFetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json();

            if (data.success) {
                dnAlert.success(successTitle, data.message);
                setPublishPicker(null);
                fetchData();
                return true;
            }

            // Blocked - the admin needs to see WHICH employee and WHY. Report
            // only (no confirm), because there is nothing to confirm: the whole
            // action was rejected.
            if (data.blocked && data.blocked.length > 0) {
                await dnAlert.report({
                    title: blockedTitle,
                    html: buildBlockedTableHtml(data),
                    wide: true,
                    danger: true,
                });
                return false;
            }

            dnAlert.error("Error", data.error || "Could not publish.");
            return false;
        } catch {
            dnAlert.error("Error", "Network error.");
            return false;
        } finally {
            setPublishing(false);
        }
    };

    // ── Confirm the publish picker ──
    // Always goes through publish_selected, even when every row is still ticked:
    // the explicit id list is what the admin just confirmed against, so sending
    // it verbatim removes any chance of publishing something the picker did not
    // show (e.g. a draft created by someone else since the page loaded).
    const handlePublishPicked = async () => {
        const ids = publishPicker?.checked || [];
        if (ids.length === 0) return;
        await runStrictPublish({
            url: "/api/payslip/management/publish_selected",
            body: { payslip_ids: ids },
            successTitle: "Published!",
            blockedTitle: "Cannot Publish",
        });
    };

    // ── Delete ──
    const handleDelete = async (payslip) => {
        if (payslip.status === "Published") {
            dnAlert.error("Cannot Delete", "Published payslips are part of the employee's permanent record and cannot be deleted.");
            return;
        }
        const ok = await dnAlert.confirm({
            title: "Delete Draft?",
            message: `Delete the draft payslip for ${payslip.emp_first_name} ${payslip.emp_last_name}? This cannot be undone.`,
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;

        try {
            const res = await apiFetch(`/api/payslip/management/delete/${payslip.payslip_id}`, { method: "DELETE" });
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Deleted!");
                fetchData();
            } else {
                dnAlert.error("Error", data.error || "Could not delete.");
            }
        } catch {
            dnAlert.error("Error", "Network error.");
        }
    };

    // ── Excel export - one sheet per month + a Grand Total sheet ──
    // ── Excel export - polished branded look matching the Employee Upload
    //    Template: DOLLUZ CORP logo at top, period banner, colour-coded
    //    section headers (Identity / Org / Attendance / Earnings / Deductions
    //    / Net / Status), frozen header rows, alternating row stripes,
    //    currency-formatted amounts, and a highlighted totals band.
    const handleExport = async () => {
        if (payslips.length === 0) {
            dnAlert.warning("Nothing to export", "Load a period with at least one payslip first.");
            return;
        }

        const wb = new ExcelJS.Workbook();
        wb.creator = "Dolluz Corp dAdmin";

        // Webpack imports for PNG return a URL - fetch it as a buffer so
        // ExcelJS can embed it. Done once; reused across sheets.
        let logoBuf = null;
        try {
            const r = await fetch(DOLLUZ_CORP);
            logoBuf = await r.arrayBuffer();
        } catch { /* logo is optional - fall back to a text banner */ }

        // Section colour palette (matches the Employee template language)
        const COLOR = {
            identity: "FFE8520A",   // orange - primary identifiers
            org:      "FF1E3A5F",   // navy - organisational columns
            attend:   "FF374151",   // slate - attendance numbers
            earn:     "FF15803D",   // green - earnings
            ded:      "FFB91C1C",   // red - deductions
            net:      "FFB45309",   // amber - net pay (highlight)
            status:   "FF6B7280",   // grey - status pill
        };

        const COLUMNS = [
            { header: "Payslip No",       key: "payslip_number",  width: 22, group: "identity" },
            { header: "Employee ID",      key: "emp_id",          width: 14, group: "identity" },
            { header: "Employee Name",    key: "emp_name",        width: 26, group: "identity" },
            { header: "Department",       key: "emp_department",  width: 22, group: "org" },
            { header: "Designation",      key: "designation",     width: 22, group: "org" },
            { header: "Location",         key: "location",        width: 14, group: "org" },
            { header: "Days In Month",    key: "days_in_month",   width: 14, group: "attend", num: true },
            { header: "LOP Days",         key: "lop_days",        width: 11, group: "attend", num: true },
            { header: "Gross Salary",     key: "gross_salary",    width: 16, group: "earn", money: true },
            { header: "Bonus",            key: "bonus",           width: 12, group: "earn", money: true },
            { header: "Other Earnings",   key: "other_earnings",  width: 16, group: "earn", money: true },
            { header: "LOP Deduction",    key: "lop_deduction",   width: 14, group: "ded", money: true },
            { header: "Loan / Advance",   key: "loan_deduction",  width: 14, group: "ded", money: true },
            { header: "Other Deductions", key: "other_deductions",width: 16, group: "ded", money: true },
            { header: "Total Deductions", key: "total_deductions",width: 16, group: "ded", money: true },
            { header: "Net Pay",          key: "net_pay",         width: 16, group: "net", money: true },
            { header: "Status",           key: "status",          width: 12, group: "status" },
            { header: "Downloaded",       key: "downloaded",      width: 13, group: "status" },
            { header: "Downloaded On",    key: "downloaded_at",   width: 22, group: "status" },
            { header: "Emailed",          key: "emailed",         width: 12, group: "status" },
            { header: "Emailed On",       key: "emailed_at",      width: 22, group: "status" },
        ];

        // ── Style helpers ──
        const thinBorder = (argb = "FFE5E7EB") => ({
            top:    { style: "thin", color: { argb } },
            left:   { style: "thin", color: { argb } },
            bottom: { style: "thin", color: { argb } },
            right:  { style: "thin", color: { argb } },
        });
        const styleHeaderCell = (cell, text, bgArgb) => {
            cell.value = text;
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            cell.border = thinBorder("FFD1D5DB");
        };
        const styleDataCell = (cell, value, opts = {}) => {
            const { money = false, num = false, rowBg = "FFFFFFFF" } = opts;
            if (money) {
                cell.value = parseFloat(value || 0);
                cell.numFmt = '"₹"#,##0.00';
                cell.alignment = { horizontal: "right", vertical: "middle" };
            } else if (num) {
                cell.value = value;
                cell.alignment = { horizontal: "center", vertical: "middle" };
            } else {
                cell.value = value ?? "";
                cell.alignment = { vertical: "middle", indent: 1 };
            }
            cell.font = { size: 9.5, color: { argb: "FF374151" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowBg } };
            cell.border = thinBorder("FFE5E7EB");
        };
        const styleStatusCell = (cell, status, rowBg) => {
            const isPub = status === "Published";
            cell.value = status;
            cell.font = { bold: true, size: 9, color: { argb: isPub ? "FF15803D" : "FFB45309" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isPub ? "FFDCFCE7" : "FFFEF3C7" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = thinBorder(rowBg === "FFFFFFFF" ? "FFE5E7EB" : "FFE5E7EB");
        };
        const colLetter = (n) =>
            n <= 26
                ? String.fromCharCode(64 + n)
                : String.fromCharCode(64 + Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26));

        const flatten = (p, periodLabel) => ({
            ...(periodLabel ? { period: periodLabel } : {}),
            payslip_number:  p.payslip_number,
            emp_id:          p.emp_id,
            emp_name:        `${p.emp_first_name || ""} ${p.emp_last_name || ""}`.trim() || p.emp_name || "",
            emp_department:  p.emp_department || p.department || "-",
            designation:     p.designation || "-",
            location:        p.location || "-",
            days_in_month:   p.days_in_month,
            lop_days:        p.lop_days,
            gross_salary:    parseFloat(p.gross_salary || 0),
            bonus:           parseFloat(p.bonus || 0),
            other_earnings:  parseFloat(p.other_earnings || 0),
            lop_deduction:   parseFloat(p.lop_deduction || 0),
            loan_deduction:  parseFloat(p.loan_deduction || 0),
            other_deductions:parseFloat(p.other_deductions || 0),
            total_deductions:parseFloat(p.total_deductions || 0),
            net_pay:         parseFloat(p.net_pay || 0),
            status:          p.status,
            downloaded:      p.status === "Published" ? (p.downloaded ? "Yes" : "No") : "-",
            downloaded_at:   p.downloaded && p.downloaded_at ? new Date(p.downloaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "",
            emailed:         p.status === "Published" ? (p.emailed ? "Yes" : "No") : "-",
            emailed_at:      p.emailed && p.emailed_at ? new Date(p.emailed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "",
        });

        // ── Render one sheet (logo + banner + headers + data + totals) ──
        const renderSheet = (ws, periodLabel, rows, includePeriodCol = false) => {
            const cols = includePeriodCol
                ? [{ header: "Period", key: "period", width: 14, group: "identity" }, ...COLUMNS]
                : COLUMNS;
            ws.columns = cols.map(c => ({ key: c.key, width: c.width }));
            ws.views = [{ state: "frozen", ySplit: 5 }];

            // Rows 1-3 - logo
            if (logoBuf) {
                try {
                    const logoId = wb.addImage({ buffer: logoBuf, extension: "png" });
                    ws.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 220, height: 60 } });
                } catch { /* ignore */ }
            }
            [1, 2, 3].forEach(r => { ws.getRow(r).height = 16; });

            // Row 4 - period banner merged across all columns
            const lastCol = colLetter(cols.length);
            ws.mergeCells(`A4:${lastCol}4`);
            const banner = ws.getCell("A4");
            banner.value = `PAYROLL REPORT - ${periodLabel}`;
            banner.font = { bold: true, size: 13, color: { argb: "FF1E3A5F" } };
            banner.alignment = { horizontal: "center", vertical: "middle" };
            banner.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
            banner.border = {
                bottom: { style: "medium", color: { argb: "FF1E3A5F" } },
            };
            ws.getRow(4).height = 28;

            // Row 5 - column headers
            const hRow = ws.getRow(5);
            hRow.height = 34;
            cols.forEach((col, i) => {
                styleHeaderCell(hRow.getCell(i + 1), col.header, COLOR[col.group]);
            });

            // Rows 6+ - data with alternating background stripes
            rows.forEach((p, idx) => {
                const dRow = ws.getRow(6 + idx);
                dRow.height = 20;
                const rowBg = idx % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB";
                cols.forEach((col, ci) => {
                    const cell = dRow.getCell(ci + 1);
                    if (col.key === "status") {
                        styleStatusCell(cell, p[col.key], rowBg);
                    } else {
                        styleDataCell(cell, p[col.key], { money: col.money, num: col.num, rowBg });
                    }
                });
            });

            // Totals band - amber-highlighted, bordered top + bottom
            const tRowNum = 6 + rows.length;
            const tRow = ws.getRow(tRowNum);
            tRow.height = 26;
            cols.forEach((col, ci) => {
                const cell = tRow.getCell(ci + 1);
                const isFirst = ci === 0;
                if (col.money) {
                    cell.value = rows.reduce((s, p) => s + parseFloat(p[col.key] || 0), 0);
                    cell.numFmt = '"₹"#,##0.00';
                    cell.alignment = { horizontal: "right", vertical: "middle" };
                } else if (isFirst) {
                    cell.value = "TOTAL";
                    cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
                } else {
                    cell.value = "";
                    cell.alignment = { horizontal: "center", vertical: "middle" };
                }
                cell.font = { bold: true, size: 10, color: { argb: "FF7C2D12" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF3C7" } };
                cell.border = {
                    top:    { style: "medium", color: { argb: "FFB45309" } },
                    bottom: { style: "medium", color: { argb: "FFB45309" } },
                    left:   { style: "thin",   color: { argb: "FFFCD34D" } },
                    right:  { style: "thin",   color: { argb: "FFFCD34D" } },
                };
            });
        };

        // ── Build sheets ──
        // For multi-month ranges, render a combined "All Payslips" sheet FIRST
        // (with a Period column) so every loaded month is visible on the
        // default tab - otherwise the file opens on the first month's sheet and
        // the other months look "missing" on later tabs.
        const isMultiMonth = groupedPayslips.filter(g => g.rows.length > 0).length > 1;
        if (isMultiMonth) {
            const wsAll = wb.addWorksheet("All Payslips");
            const allRows = [];
            groupedPayslips.forEach(g => {
                const periodLabel = `${MONTH_NAMES[g.pay_month].slice(0, 3)} ${g.pay_year}`;
                g.rows.forEach(p => allRows.push(flatten(p, periodLabel)));
            });
            const r = loadedRange || { fromYear, fromMonth, toYear, toMonth };
            const rangeLabel = `${MONTH_NAMES[r.fromMonth]} ${r.fromYear} - ${MONTH_NAMES[r.toMonth]} ${r.toYear}`;
            renderSheet(wsAll, rangeLabel, allRows, true);
        }

        // Then one sheet per month with data in the loaded range
        groupedPayslips.forEach(g => {
            if (g.rows.length === 0) return;
            const sheetName = `${MONTH_NAMES[g.pay_month].slice(0, 3)} ${g.pay_year}`;
            const ws = wb.addWorksheet(sheetName);
            const periodLabel = `${MONTH_NAMES[g.pay_month]} ${g.pay_year}`;
            renderSheet(ws, periodLabel, g.rows.map(p => flatten(p)));
        });

        // ── Download ──
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const r = loadedRange || { fromYear, fromMonth, toYear, toMonth };
        const filename = (r.fromYear === r.toYear && r.fromMonth === r.toMonth)
            ? `Payroll_${MONTH_NAMES[r.fromMonth]}_${r.fromYear}.xlsx`
            : `Payroll_${MONTH_NAMES[r.fromMonth]}-${r.fromYear}_to_${MONTH_NAMES[r.toMonth]}-${r.toYear}.xlsx`;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── PDF export (flat table across the loaded range, incl. download status) ──
    const handleExportPdf = () => {
        const cols = [
            { key: "period",         label: "Period",     pdfWidth: 66 },
            { key: "payslip_number", label: "Payslip No", pdfWidth: 92 },
            { key: "emp",            label: "Employee",   pdfWidth: 120 },
            { key: "emp_id",         label: "Emp ID",     pdfWidth: 60 },
            { key: "department",     label: "Department", pdfWidth: 92 },
            { key: "gross",          label: "Gross",      pdfWidth: 74 },
            { key: "deductions",     label: "Deductions", pdfWidth: 74 },
            { key: "net",            label: "Net Pay",    pdfWidth: 74 },
            { key: "status",         label: "Status",     pdfWidth: 52 },
            { key: "downloaded",     label: "Downloaded", pdfWidth: 56 },
            { key: "downloaded_at",  label: "Downloaded On", pdfWidth: 100 },
            { key: "emailed",        label: "Emailed",    pdfWidth: 56 },
        ];
        const rows = [];
        groupedPayslips.forEach(g => {
            if (!g.rows.length) return;
            const period = `${MONTH_NAMES[g.pay_month].slice(0, 3)} ${g.pay_year}`;
            g.rows.forEach(p => rows.push({
                period,
                payslip_number: p.payslip_number,
                emp: `${p.emp_first_name || ""} ${p.emp_last_name || ""}`.trim() || p.emp_name || "",
                emp_id: p.emp_id,
                department: p.emp_department || p.department || "-",
                // jsPDF's built-in fonts have no ₹ glyph (renders as "¹"), so
                // use "Rs." in the exported PDF.
                gross: `Rs. ${fmt(p.gross_salary)}`,
                deductions: `Rs. ${fmt(p.total_deductions)}`,
                net: `Rs. ${fmt(p.net_pay)}`,
                status: p.status,
                downloaded: p.status === "Published" ? (p.downloaded ? "Yes" : "No") : "-",
                downloaded_at: p.downloaded && p.downloaded_at ? new Date(p.downloaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "",
                emailed: p.status === "Published" ? (p.emailed ? "Yes" : "No") : "-",
            }));
        });
        const r = loadedRange || { fromYear, fromMonth, toYear, toMonth };
        const empLbl = appliedEmpFilter ? `   ·   Employee: ${empFilterOptions.find(o => o.value === appliedEmpFilter)?.label || appliedEmpFilter}` : "";
        const rangeLbl = `${MONTH_NAMES[r.fromMonth]} ${r.fromYear} - ${MONTH_NAMES[r.toMonth]} ${r.toYear}${empLbl}`;
        exportTicketsPdf({ moduleName: "dSlip", sheetTitle: "Payslip Management", columns: cols, rows, filterLabel: rangeLbl, statusKey: "status", filename: `Payroll_${new Date().toISOString().slice(0, 10)}.pdf` });
    };

    // ── Derived ──
    const draftCount = payslips.filter(p => p.status === "Draft").length;
    const publishedCount = payslips.filter(p => p.status === "Published").length;
    // Both bulk actions target the whole loaded period, not the filtered view,
    // so their counts always match the summary cards above them. Narrowing is
    // done inside each action's picker rather than by filtering the table.
    const allPublishedIds = payslips.filter(p => p.status === "Published").map(p => p.payslip_id);
    const labelForLoaded = loadedRange
        ? rangeLabel(loadedRange.fromYear, loadedRange.fromMonth, loadedRange.toYear, loadedRange.toMonth)
        : rangeLabel(fromYear, fromMonth, toYear, toMonth);

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div className="pm-container">
            <div className={`pm-dashboard ${navSize}`}>
                <div className="pm-content">

                    {/* ── Page header ── */}
                    <div className="pm-page-header">
                        <div className="pm-page-header-left">
                            <h2>
                                <a className="navbar-brand" href="#">
                                    <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                                </a>
                                Payslip Management
                            </h2>
                            <p className="pm-subtitle">
                                Review, edit, publish, and export payslips for any pay period.
                            </p>
                        </div>
                    </div>

                    {/* ── Filter bar (From / To range) ── */}
                    <div className="pm-filter-bar">
                        <div className="pm-field-group pm-field-range">
                            <label>From</label>
                            <div className="pm-range-row">
                                <Select
                                    options={MONTH_SELECT_OPTIONS}
                                    value={MONTH_SELECT_OPTIONS.find(o => o.value === fromMonth)}
                                    onChange={opt => { setFromMonth(opt.value); }}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    styles={PM_MONTH_STYLES}
                                />
                                <Select
                                    options={YEAR_SELECT_OPTIONS}
                                    value={YEAR_SELECT_OPTIONS.find(o => o.value === fromYear)}
                                    onChange={opt => { setFromYear(opt.value); }}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    styles={PM_YEAR_STYLES}
                                />
                            </div>
                        </div>
                        <div className="pm-field-group pm-field-range">
                            <label>To</label>
                            <div className="pm-range-row">
                                <Select
                                    options={MONTH_SELECT_OPTIONS}
                                    value={MONTH_SELECT_OPTIONS.find(o => o.value === toMonth)}
                                    onChange={opt => { setToMonth(opt.value); }}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    styles={PM_MONTH_STYLES}
                                />
                                <Select
                                    options={YEAR_SELECT_OPTIONS}
                                    value={YEAR_SELECT_OPTIONS.find(o => o.value === toYear)}
                                    onChange={opt => { setToYear(opt.value); }}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    styles={PM_YEAR_STYLES}
                                />
                            </div>
                        </div>
                        <div className="pm-field-group pm-field-emp">
                            <label>Employee</label>
                            <Select
                                options={empFilterOptions}
                                value={empFilterOptions.find(o => o.value === empFilter) || empFilterOptions[0]}
                                onChange={opt => setEmpFilter(opt?.value || "")}
                                isSearchable
                                menuPortalTarget={document.body}
                                styles={PM_EMP_STYLES}
                                placeholder="All employees"
                            />
                        </div>
                        <button
                            className="pm-btn-load"
                            onClick={handleLoad}
                            disabled={loading || rangeInverted}
                            title={rangeInverted ? "End must be on or after Start" : ""}
                        >
                            {loading ? "Loading…" : "Load Payslips"}
                        </button>

                        {loaded && payslips.length > 0 && (
                            <div className="pm-action-btns">
                                {/* Two bulk actions only. Each opens a picker with
                                    every candidate pre-ticked, so "All" is the
                                    default and narrowing happens there. */}
                                <button
                                    className="pm-btn-publish-all"
                                    onClick={openPublishPicker}
                                    disabled={publishing || draftCount === 0}
                                    title={draftCount === 0 ? "No drafts left to publish" : `Choose which drafts in ${labelForLoaded} to publish`}
                                >
                                    <FaCheckDouble style={{ marginRight: 6 }} />
                                    {publishing ? "Publishing…" : `Publish All (${draftCount})`}
                                </button>
                                <button
                                    className="pm-btn-send-email"
                                    onClick={() => openEmailModal(allPublishedIds)}
                                    disabled={allPublishedIds.length === 0}
                                    title={allPublishedIds.length === 0
                                        ? "No published payslips to remind about"
                                        : "Choose which employees to remind, then preview and send"}
                                >
                                    <FaEnvelope style={{ marginRight: 6 }} />
                                    Remind All{allPublishedIds.length ? ` (${allPublishedIds.length})` : ""}
                                </button>
                                <button
                                    className="pm-btn-maillog"
                                    onClick={openMailLog}
                                    title="Every payslip email sent, with filters"
                                >
                                    <FaHistory style={{ marginRight: 6 }} />
                                    Email History
                                </button>
                                <ExportMenu onExcel={handleExport} onPdf={handleExportPdf} />
                            </div>
                        )}
                    </div>

                    {rangeInverted && (
                        <div className="pm-range-warn">End month must be on or after Start month.</div>
                    )}

                    {/* ── Summary cards (grand totals across range) ── */}
                    {loaded && summary && (
                        <div className="pm-summary-cards">
                            <div className="pm-summary-card">
                                <div className="pm-summary-card-label">Total Payslips</div>
                                <div className="pm-summary-card-val">{summary.total || 0}</div>
                                <div className="pm-summary-card-sub">
                                    {(summary.months || []).filter(m => m.total > 0).length} month{(summary.months || []).filter(m => m.total > 0).length !== 1 ? "s" : ""}
                                </div>
                            </div>
                            <div className="pm-summary-card pm-summary-card--yellow">
                                <div className="pm-summary-card-label">Draft</div>
                                <div className="pm-summary-card-val">{draftCount}</div>
                                <div className="pm-summary-card-sub">Pending review</div>
                            </div>
                            <div className="pm-summary-card pm-summary-card--green">
                                <div className="pm-summary-card-label">Published</div>
                                <div className="pm-summary-card-val">{publishedCount}</div>
                                <div className="pm-summary-card-sub">Visible to employees</div>
                            </div>
                            <div className="pm-summary-card pm-summary-card--blue">
                                <div className="pm-summary-card-label">Total Net Pay</div>
                                <div className="pm-summary-card-val pm-summary-card-val--small">
                                    ₹{fmt(summary.total_net_pay)}
                                </div>
                                <div className="pm-summary-card-sub">{labelForLoaded}</div>
                            </div>
                        </div>
                    )}

                    {/* ── Status filter chips ── */}
                    {loaded && payslips.length > 0 && (
                        <div className="pm-status-filter">
                            {[
                                { v: "all", label: "All", count: payslips.length },
                                { v: "draft", label: "Draft", count: draftCount },
                                { v: "published", label: "Published", count: publishedCount },
                                { v: "emailed", label: "Emailed", count: payslips.filter(p => p.emailed).length },
                                { v: "not_emailed", label: "Not emailed", count: payslips.filter(p => p.status === "Published" && !p.emailed).length },
                            ].map(opt => (
                                <button
                                    key={opt.v}
                                    type="button"
                                    className={`pm-status-chip ${statusFilter === opt.v ? `pm-status-chip--active pm-status-chip--${opt.v}` : ""}`}
                                    onClick={() => setStatusFilter(opt.v)}
                                >
                                    {opt.label}
                                    <span className="pm-status-chip-count">{opt.count}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* ── Table area - one section card per month in the range ──
                         Empty / loading / no-results states get their own framed
                         card; the grouped sections render unwrapped because each
                         month-section is already styled as a standalone card. */}
                    {!loaded ? (
                        <div className="pm-card">
                            <div className="pm-empty-state">
                                <div className="pm-empty-icon">📋</div>
                                <div className="pm-empty-title">Select a period to load payslips</div>
                                <p className="pm-empty-desc">Choose a From and To month above, then click Load Payslips.</p>
                            </div>
                        </div>
                    ) : loading ? (
                        <div className="pm-card">
                            <div className="pm-loading">Loading payslips…</div>
                        </div>
                    ) : payslips.length === 0 ? (
                        <div className="pm-card">
                            <div className="pm-empty-state">
                                <div className="pm-empty-icon">🗂</div>
                                <div className="pm-empty-title">No payslips found</div>
                                <p className="pm-empty-desc">
                                    No payslips exist for {labelForLoaded}.{" "}
                                    <span
                                        className="pm-link"
                                        onClick={() => navigate("/Payslip_Generate")}
                                    >
                                        Generate them now →
                                    </span>
                                </p>
                            </div>
                        </div>
                    ) : groupedPayslips.length === 0 ? (
                        <div className="pm-card">
                            <div className="pm-empty-state">
                                <div className="pm-empty-icon">🔍</div>
                                <div className="pm-empty-title">No payslips match this filter</div>
                                <p className="pm-empty-desc">
                                    Nothing is{" "}
                                    {statusFilter === "draft" ? "in Draft" :
                                        statusFilter === "published" ? "Published" :
                                            statusFilter === "emailed" ? "emailed yet" :
                                                statusFilter === "not_emailed" ? "pending an email" : "here"}{" "}
                                    for {labelForLoaded}.{" "}
                                    <span className="pm-link" onClick={() => setStatusFilter("all")}>Show all →</span>
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="pm-month-sections">
                                {groupedPayslips.map(g => {
                                    const isEmpty = g.rows.length === 0;
                                    return (
                                    <div
                                        className={`pm-month-section ${isEmpty ? "pm-month-section--empty" : ""}`}
                                        key={`${g.pay_year}-${g.pay_month}`}
                                    >
                                        {/* Per-month header band */}
                                        <div className="pm-month-header">
                                            <div className="pm-month-title">{MONTH_NAMES[g.pay_month]} {g.pay_year}</div>
                                            <div className="pm-month-stats">
                                                {isEmpty ? (
                                                    <span className="pm-month-empty-inline">No payslips generated yet</span>
                                                ) : (
                                                    <>
                                                        <span className="pm-month-stat">{g.summary?.total || 0} payslips</span>
                                                        <span className="pm-month-stat pm-month-stat--draft">{g.summary?.draft_count || 0} draft</span>
                                                        <span className="pm-month-stat pm-month-stat--pub">{g.summary?.published_count || 0} published</span>
                                                        <span className="pm-month-stat pm-month-stat--net">Net ₹{fmt(g.summary?.total_net_pay || 0)}</span>
                                                        {g.summary?.batch_number && (
                                                            <span className="pm-month-stat pm-month-stat--batch">{g.summary.batch_number}</span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {!isEmpty && (
                                            <div className="pm-table-wrapper">
                                                <table className="pm-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Payslip No</th>
                                                            <th>Employee</th>
                                                            <th>Department</th>
                                                            <th>Gross Salary</th>
                                                            <th>Total Deductions</th>
                                                            <th>Net Pay</th>
                                                            <th>Status</th>
                                                            <th>Downloaded</th>
                                                            <th>Emailed</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {g.rows.map(p => (
                                                            <tr key={p.payslip_id}>
                                                                <td>
                                                                    <span className="pm-payslip-num">{p.payslip_number}</span>
                                                                </td>
                                                                <td>
                                                                    <div className="pm-emp-cell">
                                                                        <div className="pm-avatar">
                                                                            {p.emp_first_name?.[0]?.toUpperCase()}
                                                                        </div>
                                                                        <div>
                                                                            <div className="pm-emp-name">{p.emp_first_name} {p.emp_last_name}</div>
                                                                            <div className="pm-emp-id">{p.emp_id}</div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{p.emp_department || "-"}</td>
                                                                <td className="pm-num">₹{fmt(p.gross_salary)}</td>
                                                                <td className="pm-num pm-red">₹{fmt(p.total_deductions)}</td>
                                                                <td className="pm-num pm-bold">₹{fmt(p.net_pay)}</td>
                                                                <td>
                                                                    <span className={`pm-pill ${p.status === "Published" ? "pill-green" : "pill-yellow"}`}>
                                                                        {p.status}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {/* Clickable in both states, mirroring the Emailed
                                                                        cell - the download log is worth reading even
                                                                        when nothing has been downloaded. */}
                                                                    <button
                                                                        type="button"
                                                                        className={`pm-mail-state ${p.downloaded ? "pm-mail-state--dl" : "pm-mail-state--none"}`}
                                                                        onClick={() => openDownloadHistory(p)}
                                                                        title={p.downloaded
                                                                            ? `View download history${p.downloaded_at ? ` · last ${new Date(p.downloaded_at).toLocaleString("en-IN")}` : ""}`
                                                                            : "View download history - not downloaded yet"}
                                                                    >
                                                                        {p.downloaded
                                                                            ? <><FaCheckCircle /> Downloaded{p.download_count > 1 ? ` (${p.download_count})` : ""}</>
                                                                            : <><FaHistory /> Not downloaded</>}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    {/* Always a history button, whichever state the row is
                                                                        in - the send log is worth reading even when nothing
                                                                        has been sent, and a bare "—" hid that entirely. */}
                                                                    <button
                                                                        type="button"
                                                                        className={`pm-mail-state ${p.emailed ? "pm-mail-state--sent" : "pm-mail-state--none"}`}
                                                                        onClick={() => openEmailHistory(p)}
                                                                        title={p.emailed
                                                                            ? `View send history${p.email_count > 1 ? ` · ${p.email_count} sends` : ""}`
                                                                            : "View send history - nothing sent yet"}
                                                                    >
                                                                        {p.emailed
                                                                            ? <><FaCheckCircle /> Sent{p.email_count > 1 ? ` (${p.email_count})` : ""}</>
                                                                            : <><FaHistory /> No mails sent</>}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    {/* Fixed set of actions in a fixed order on every row -
                                                                        buttons that don't apply are disabled, not hidden, so
                                                                        the icons never shift position between a Draft row and
                                                                        a Published one. */}
                                                                    <div className="pm-actions">
                                                                        {(() => {
                                                                            const isDraft = p.status === "Draft";
                                                                            const isPub = p.status === "Published";
                                                                            const draftOnly = isDraft ? undefined : "Only available while the payslip is a draft";
                                                                            const pubOnly = isPub ? undefined : "Available once the payslip is published";
                                                                            return (
                                                                                <>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--view"
                                                                                        title="Payslip preview"
                                                                                        onClick={() => setPreviewId(p.payslip_id)}
                                                                                    >
                                                                                        <FaEye />
                                                                                    </button>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--edit"
                                                                                        title={draftOnly || "Edit"}
                                                                                        disabled={!isDraft}
                                                                                        onClick={() => setEditPayslip(p)}
                                                                                    >
                                                                                        <FaEdit />
                                                                                    </button>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--publish"
                                                                                        title={draftOnly || "Publish"}
                                                                                        disabled={!isDraft || publishing}
                                                                                        onClick={() => handlePublishOne(p)}
                                                                                    >
                                                                                        <FaCheckCircle />
                                                                                    </button>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--email"
                                                                                        title={pubOnly || "Email preview"}
                                                                                        disabled={!isPub}
                                                                                        onClick={() => openEmailModal([p.payslip_id], { previewOnly: true })}
                                                                                    >
                                                                                        <FaRegEnvelopeOpen />
                                                                                    </button>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--send"
                                                                                        title={pubOnly || "Send payslip email"}
                                                                                        disabled={!isPub}
                                                                                        onClick={() => handleSendOne(p)}
                                                                                    >
                                                                                        <FaPaperPlane />
                                                                                    </button>
                                                                                    <button
                                                                                        className="pm-icon-btn pm-icon-btn--delete"
                                                                                        title={draftOnly || "Delete"}
                                                                                        disabled={!isDraft}
                                                                                        onClick={() => handleDelete(p)}
                                                                                    >
                                                                                        <FaTrash />
                                                                                    </button>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                        </div>
                    )}

                </div>
            </div>

            {/* ── Modals ── */}
            {previewId && (
                <PreviewModal
                    payslipId={previewId}
                    onClose={() => setPreviewId(null)}
                />
            )}
            {editPayslip && (
                <EditModal
                    payslip={editPayslip}
                    onClose={() => setEditPayslip(null)}
                    onSaved={() => { fetchData(); }}
                />
            )}

            {/* ── Payslip email preview + send modal ── */}
            {emailModal && (
                <div className="pm-email-overlay">
                    {/* One recipient (the row ✉ button, or a Remind All that only
                        matched one) collapses to a plain send: no filters, no
                        checklist, no pager - there is nothing to choose between.
                        Those controls only appear once there is more than one. */}
                    <div className={`pm-email-modal${emailModal.recipients?.length > 1 ? " pm-remind-modal" : ""}`}>
                        <div className="pm-email-head">
                            <div>
                                <div className="pm-email-title">
                                    <FaEnvelope style={{ marginRight: 8 }} />
                                    {emailModal.previewOnly
                                        ? "Email Preview"
                                        : emailModal.recipients?.length === 1 ? "Send Payslip Email" : "Remind Employees"}
                                </div>
                                <div className="pm-email-sub">
                                    {emailModal.recipients?.length === 1
                                        ? `${emailModal.recipients[0].employee_name} · ${emailModal.recipients[0].month_label}`
                                        : <>
                                            {`${emailModal.recipients?.length || emailModal.ids.length} recipients`}
                                            {emailModal.checked && <> · {emailModal.checked.length} selected</>}
                                            {emailModal.noEmailCount > 0 && <> · {emailModal.noEmailCount} without an email</>}
                                            {emailModal.notPublishedCount > 0 && <> · {emailModal.notPublishedCount} not published</>}
                                        </>}
                                </div>
                            </div>
                            <button className="pm-email-close" onClick={() => !emailModal.sending && setEmailModal(null)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="pm-email-body">
                            {emailModal.loading ? (
                                <div className="pm-email-loading">Building preview…</div>
                            ) : emailModal.error ? (
                                <div className="pm-email-error">{emailModal.error}</div>
                            ) : emailModal.result ? (
                                <div className="pm-email-result">
                                    {emailModal.result.error ? (
                                        <div className="pm-email-error">{emailModal.result.error}</div>
                                    ) : (
                                        <>
                                            <div className="pm-email-result-row pm-email-result-sent">
                                                <FaCheckCircle /> {emailModal.result.sent} sent
                                            </div>
                                            {emailModal.result.failed > 0 && (
                                                <div className="pm-email-result-row pm-email-result-failed">
                                                    {emailModal.result.failed} failed
                                                </div>
                                            )}
                                            {emailModal.result.skipped > 0 && (
                                                <div className="pm-email-result-row pm-email-result-skipped">
                                                    {emailModal.result.skipped} skipped (draft / no email)
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ) : emailModal.recipients?.length ? (() => {
                                // The recipient currently being previewed. Every
                                // recipient is reachable via the pager below, so a
                                // bulk send is never sent blind.
                                const total = emailModal.recipients.length;
                                const rcpt = emailModal.recipients[emailModal.index] || emailModal.recipients[0];
                                const prev = emailModal.previews?.[rcpt.payslip_id];
                                const shownR = emailModal.recipients.filter(r => passesPickFilter(r, mailFilters));
                                const shownElig = shownR.filter(r => r.sendable);
                                const shownOnR = shownElig.filter(r => emailModal.checked.includes(r.payslip_id));
                                return (
                                    <>
                                        {total > 1 && (
                                        <PickerFilters
                                            rows={shownR}
                                            filters={mailFilters}
                                            onChange={setMailFilters}
                                            empOptions={pickEmpOptions(emailModal.recipients)}
                                        />
                                        )}

                                        {/* Who gets the reminder. Same columns as the
                                            publish picker so the two read alike;
                                            pre-ticked for everyone eligible, untick to
                                            narrow. Click a row to include/exclude, the
                                            eye to preview that person's email below. */}
                                        {total > 1 && (
                                        <div className="pm-rcpt-panel">
                                            <div className="pm-rcpt-head">
                                                <label className="pm-picker-all">
                                                    <input
                                                        type="checkbox"
                                                        checked={shownElig.length > 0 && shownOnR.length === shownElig.length}
                                                        ref={el => {
                                                            if (el) el.indeterminate =
                                                                shownOnR.length > 0 && shownOnR.length < shownElig.length;
                                                        }}
                                                        disabled={shownElig.length === 0}
                                                        onChange={() => toggleAllRecipients(shownR)}
                                                    />
                                                    Select all shown
                                                </label>
                                                <span className="pm-rcpt-count">
                                                    {emailModal.checked.length} will be sent
                                                </span>
                                            </div>
                                            {shownR.length === 0 ? (
                                                <div className="pm-email-loading">No recipients match these filters.</div>
                                            ) : (
                                            <div className="pm-rcpt-list">
                                                <table className="pm-picker-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 34 }}></th>
                                                            <th>Employee</th>
                                                            <th>Payslip</th>
                                                            <th>Month</th>
                                                            <th className="pm-num">Net Pay</th>
                                                            <th style={{ width: 44 }}>Preview</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {shownR.map(r => {
                                                            const i = emailModal.recipients.indexOf(r);
                                                            const on = emailModal.checked.includes(r.payslip_id);
                                                            return (
                                                                <tr
                                                                    key={r.payslip_id}
                                                                    className={`${on ? "pm-picker-row--on" : ""}${i === emailModal.index ? " pm-rcpt-row--active" : ""}${!r.sendable ? " pm-rcpt-row--skip" : ""}`}
                                                                    // Row click ticks / unticks, matching the publish
                                                                    // picker. Previewing is the eye button, so the two
                                                                    // actions never fight over the same click.
                                                                    onClick={() => r.sendable && toggleRecipient(r.payslip_id)}
                                                                    title={r.sendable ? "Click to include or exclude" : undefined}
                                                                >
                                                                    <td>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={on}
                                                                            disabled={!r.sendable}
                                                                            onChange={() => toggleRecipient(r.payslip_id)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title={r.sendable ? "Include in this send" :
                                                                                (r.status !== "Published" ? "Not published" : "No email address on file")}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <div className="pm-emp-name">{r.employee_name}</div>
                                                                        <div className="pm-emp-id">
                                                                            {r.emp_id} · {r.department || "-"}
                                                                            {!r.sendable && (
                                                                                <span className="pm-rcpt-skiptag">
                                                                                    {r.status !== "Published" ? "not published" : "no email"}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td><span className="pm-payslip-num">{r.payslip_number}</span></td>
                                                                    <td>{r.month_label}</td>
                                                                    <td className="pm-num pm-bold">₹{fmt(r.net_pay)}</td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className={`pm-icon-btn pm-icon-btn--view${i === emailModal.index ? " pm-icon-btn--on" : ""}`}
                                                                            title="Preview this employee's email below"
                                                                            onClick={(e) => { e.stopPropagation(); loadPreviewAt(i); }}
                                                                        >
                                                                            <FaEye />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            )}
                                        </div>
                                        )}

                                        {total > 1 && (
                                            <div className="pm-email-pager">
                                                <button
                                                    type="button"
                                                    className="pm-email-pager-btn"
                                                    onClick={() => stepPreview(-1)}
                                                    disabled={emailModal.index === 0}
                                                    title="Previous recipient"
                                                >‹</button>
                                                <div className="pm-email-pager-mid">
                                                    <span className="pm-email-pager-pos">
                                                        Recipient {emailModal.index + 1} of {total}
                                                    </span>
                                                    <span className="pm-email-pager-name">
                                                        {rcpt.employee_name}
                                                        <span className="pm-email-pager-meta"> · {rcpt.emp_id} · {rcpt.month_label}</span>
                                                    </span>
                                                </div>
                                                {(() => {
                                                    const opts = emailModal.recipients.map((r, i) => ({
                                                        value: i,
                                                        label: `${i + 1}. ${r.employee_name}${r.sendable ? "" : " (will skip)"}`,
                                                    }));
                                                    return (
                                                        <Select
                                                            options={opts}
                                                            value={opts[emailModal.index]}
                                                            onChange={(opt) => loadPreviewAt(opt.value)}
                                                            isSearchable
                                                            menuPortalTarget={document.body}
                                                            menuPlacement="auto"
                                                            styles={PM_RCPT_STYLES}
                                                            placeholder="Jump to…"
                                                        />
                                                    );
                                                })()}
                                                <button
                                                    type="button"
                                                    className="pm-email-pager-btn"
                                                    onClick={() => stepPreview(1)}
                                                    disabled={emailModal.index >= total - 1}
                                                    title="Next recipient"
                                                >›</button>
                                            </div>
                                        )}

                                        {!rcpt.sendable && (
                                            <div className="pm-email-warn">
                                                {rcpt.status !== "Published"
                                                    ? <>This payslip is <strong>{rcpt.status}</strong> - it will be skipped. Only published payslips are sent.</>
                                                    : <>No email address on file for <strong>{rcpt.employee_name}</strong> - this one will be skipped.</>}
                                            </div>
                                        )}

                                        {emailModal.previewLoading && !prev ? (
                                            <div className="pm-email-loading">Rendering {rcpt.employee_name}'s email…</div>
                                        ) : prev?.error ? (
                                            <div className="pm-email-error">{prev.error}</div>
                                        ) : prev ? (
                                            <>
                                                <div className="pm-email-meta">
                                                    <div className="pm-email-meta-row">
                                                        <span className="pm-email-meta-label">From</span>
                                                        <span className="pm-email-meta-val">{prev.from}</span>
                                                    </div>
                                                    <div className="pm-email-meta-row">
                                                        <span className="pm-email-meta-label">To</span>
                                                        <span className="pm-email-meta-val">
                                                            {prev.to || <em style={{ color: "#dc2626" }}>No email on file</em>}
                                                        </span>
                                                    </div>
                                                    <div className="pm-email-meta-row">
                                                        <span className="pm-email-meta-label">Cc</span>
                                                        <span className="pm-email-meta-val">{(prev.cc || []).join(", ")}</span>
                                                    </div>
                                                    <div className="pm-email-meta-row">
                                                        <span className="pm-email-meta-label">Subject</span>
                                                        <span className="pm-email-meta-val pm-email-meta-subject">{prev.subject}</span>
                                                    </div>
                                                </div>
                                                <iframe
                                                    title={`Payslip email preview - ${rcpt.employee_name}`}
                                                    className="pm-email-iframe"
                                                    srcDoc={prev.html}
                                                />
                                            </>
                                        ) : null}
                                    </>
                                );
                            })() : null}
                        </div>

                        <div className="pm-email-foot">
                            {emailModal.previewOnly ? (
                                <button className="pm-email-btn pm-email-btn--primary" onClick={() => setEmailModal(null)}>
                                    Close
                                </button>
                            ) : emailModal.result ? (
                                <button className="pm-email-btn pm-email-btn--primary" onClick={() => setEmailModal(null)}>
                                    Done
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="pm-email-btn pm-email-btn--ghost"
                                        onClick={() => setEmailModal(null)}
                                        disabled={emailModal.sending}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="pm-email-btn pm-email-btn--primary"
                                        onClick={handleSendEmails}
                                        disabled={
                                            emailModal.sending || emailModal.loading || !!emailModal.error ||
                                            !emailModal.checked?.length
                                        }
                                        title={!emailModal.checked?.length
                                            ? "Tick at least one recipient"
                                            : undefined}
                                    >
                                        <FaPaperPlane style={{ marginRight: 6 }} />
                                        {emailModal.sending
                                            ? "Sending…"
                                            : `Send (${emailModal.checked?.length || 0})`}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Publish picker ──
                 Opened by Publish All. Lists every draft in the loaded period
                 pre-ticked; untick to narrow. Publishing is strict + atomic, so
                 if any ticked row's previous month is unpublished the whole run
                 is rejected and the blocker table is shown instead. */}
            {publishPicker && (() => {
                const shown = publishPicker.rows.filter(r => passesPickFilter(r, pickFilters));
                const shownIds = shown.map(r => r.payslip_id);
                const shownOn = shownIds.filter(id => publishPicker.checked.includes(id));
                const hiddenOn = publishPicker.checked.length - shownOn.length;
                return (
                <div className="pm-email-overlay">
                    <div className="pm-email-modal pm-picker-modal">
                        <div className="pm-email-head">
                            <div>
                                <div className="pm-email-title">
                                    <FaCheckDouble style={{ marginRight: 8 }} />
                                    Publish Payslips
                                </div>
                                <div className="pm-email-sub">
                                    {publishPicker.rows.length} draft{publishPicker.rows.length !== 1 ? "s" : ""} in {labelForLoaded}
                                    {" · "}{publishPicker.checked.length} selected
                                </div>
                            </div>
                            <button className="pm-email-close" onClick={() => !publishing && setPublishPicker(null)}>
                                <FaTimes />
                            </button>
                        </div>

                        <PickerFilters
                            rows={shown}
                            filters={pickFilters}
                            onChange={setPickFilters}
                            empOptions={pickEmpOptions(publishPicker.rows.map(r => ({
                                emp_id: r.emp_id,
                                employee_name: `${r.emp_first_name || ""} ${r.emp_last_name || ""}`.trim(),
                            })))}
                        />

                        <div className="pm-picker-bar">
                            <label className="pm-picker-all">
                                <input
                                    type="checkbox"
                                    checked={shownIds.length > 0 && shownOn.length === shownIds.length}
                                    ref={el => {
                                        if (el) el.indeterminate = shownOn.length > 0 && shownOn.length < shownIds.length;
                                    }}
                                    disabled={shownIds.length === 0}
                                    onChange={() => togglePublishPickAll(shown)}
                                />
                                Select all shown
                            </label>
                            <span className="pm-picker-hint">
                                Employees can download a payslip as soon as it is published.
                            </span>
                        </div>

                        <div className="pm-email-body">
                            {shown.length === 0 ? (
                                <div className="pm-email-loading">No drafts match these filters.</div>
                            ) : (
                            <table className="pm-picker-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 34 }}></th>
                                        <th>Employee</th>
                                        <th>Payslip</th>
                                        <th>Month</th>
                                        <th className="pm-num">Net Pay</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map(r => {
                                        const on = publishPicker.checked.includes(r.payslip_id);
                                        return (
                                            <tr
                                                key={r.payslip_id}
                                                className={on ? "pm-picker-row--on" : ""}
                                                onClick={() => togglePublishPick(r.payslip_id)}
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={on}
                                                        onChange={() => togglePublishPick(r.payslip_id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="pm-emp-name">{r.emp_first_name} {r.emp_last_name}</div>
                                                    <div className="pm-emp-id">{r.emp_id} · {r.emp_department || "-"}</div>
                                                </td>
                                                <td><span className="pm-payslip-num">{r.payslip_number}</span></td>
                                                <td>{MONTH_NAMES[r.pay_month]} {r.pay_year}</td>
                                                <td className="pm-num pm-bold">₹{fmt(r.net_pay)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            )}
                        </div>

                        <div className="pm-email-foot">
                            {hiddenOn > 0 && (
                                <span className="pm-picker-hidden">
                                    {hiddenOn} ticked row{hiddenOn !== 1 ? "s are" : " is"} hidden by the filter and will still publish
                                </span>
                            )}
                            <button
                                className="pm-email-btn pm-email-btn--ghost"
                                onClick={() => setPublishPicker(null)}
                                disabled={publishing}
                            >
                                Cancel
                            </button>
                            <button
                                className="pm-email-btn pm-email-btn--primary"
                                onClick={handlePublishPicked}
                                disabled={publishing || publishPicker.checked.length === 0}
                            >
                                <FaCheckCircle style={{ marginRight: 6 }} />
                                {publishing ? "Publishing…" : `Publish (${publishPicker.checked.length})`}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* ── Full email log (all payslips, filterable) ── */}
            {mailLog && (
                <div className="pm-email-overlay">
                    <div className="pm-email-modal pm-maillog-modal">
                        <div className="pm-email-head">
                            <div>
                                <div className="pm-email-title">
                                    <FaHistory style={{ marginRight: 8 }} />
                                    Email History
                                </div>
                                <div className="pm-email-sub">
                                    Every payslip email sent for {labelForLoaded}
                                    {mailLog.loading ? "" : ` · ${mailLog.total} record${mailLog.total !== 1 ? "s" : ""}`}
                                </div>
                            </div>
                            <button className="pm-email-close" onClick={() => setMailLog(null)}>
                                <FaTimes />
                            </button>
                        </div>

                        {/* Filters - all applied server-side, so they stay correct
                            once the log has thousands of rows. Employee /
                            Department / Status options come from the log itself,
                            so they never offer a value that returns nothing. */}
                        <div className="pm-maillog-filters">
                            <div className="pm-maillog-field pm-maillog-field--range">
                                <label>From</label>
                                <div className="pm-maillog-range">
                                    <Select
                                        options={MONTH_SELECT_OPTIONS}
                                        value={MONTH_SELECT_OPTIONS.find(o => o.value === mailLog.filters.fromMonth)}
                                        onChange={opt => setMailLogFilter({ fromMonth: opt.value })}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={PM_LOG_MONTH_STYLES}
                                    />
                                    <Select
                                        options={YEAR_SELECT_OPTIONS}
                                        value={YEAR_SELECT_OPTIONS.find(o => o.value === mailLog.filters.fromYear)}
                                        onChange={opt => setMailLogFilter({ fromYear: opt.value })}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={PM_LOG_YEAR_STYLES}
                                    />
                                </div>
                            </div>
                            <div className="pm-maillog-field pm-maillog-field--range">
                                <label>To</label>
                                <div className="pm-maillog-range">
                                    <Select
                                        options={MONTH_SELECT_OPTIONS}
                                        value={MONTH_SELECT_OPTIONS.find(o => o.value === mailLog.filters.toMonth)}
                                        onChange={opt => setMailLogFilter({ toMonth: opt.value })}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={PM_LOG_MONTH_STYLES}
                                    />
                                    <Select
                                        options={YEAR_SELECT_OPTIONS}
                                        value={YEAR_SELECT_OPTIONS.find(o => o.value === mailLog.filters.toYear)}
                                        onChange={opt => setMailLogFilter({ toYear: opt.value })}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        styles={PM_LOG_YEAR_STYLES}
                                    />
                                </div>
                            </div>

                            <div className="pm-maillog-field">
                                <label>Employee</label>
                                {(() => {
                                    const opts = [
                                        { value: "", label: "All employees" },
                                        ...(mailLog.employees || []).map(e => ({ value: e.emp_id, label: `${e.name} (${e.emp_id})` })),
                                    ];
                                    return (
                                        <Select
                                            options={opts}
                                            value={opts.find(o => o.value === (mailLog.filters.emp_id || "")) || opts[0]}
                                            onChange={(opt) => setMailLogFilter({ emp_id: opt?.value || "" })}
                                            isSearchable
                                            menuPortalTarget={document.body}
                                            styles={PM_LOG_EMP_STYLES}
                                        />
                                    );
                                })()}
                            </div>

                            <div className="pm-maillog-field pm-maillog-field--grow">
                                <label>Search</label>
                                <input
                                    className="pm-maillog-search"
                                    type="search"
                                    placeholder="Recipient, subject or payslip no…"
                                    defaultValue={mailLog.filters.q || ""}
                                    onKeyDown={(e) => { if (e.key === "Enter") setMailLogFilter({ q: e.currentTarget.value.trim() }); }}
                                    onBlur={(e) => {
                                        const v = e.currentTarget.value.trim();
                                        if (v !== (mailLog.filters.q || "")) setMailLogFilter({ q: v });
                                    }}
                                />
                            </div>

                            {(mailLog.filters.emp_id || mailLog.filters.q) && (
                                <button type="button" className="pm-maillog-reset" onClick={resetMailLogFilters}>
                                    Clear filters
                                </button>
                            )}
                        </div>

                        <div className="pm-email-body">
                            {mailLog.loading ? (
                                <div className="pm-email-loading">Loading email history…</div>
                            ) : mailLog.error ? (
                                <div className="pm-email-error">{mailLog.error}</div>
                            ) : !mailLog.rows?.length ? (
                                <div className="pm-email-loading">
                                    No payslip emails match these filters.
                                </div>
                            ) : (
                                <table className="pm-history-table">
                                    <thead>
                                        <tr>
                                            <th>Sent At</th>
                                            <th>Employee</th>
                                            <th>Payslip</th>
                                            <th>To</th>
                                            <th>Status</th>
                                            <th>By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mailLog.rows.map(r => (
                                            <tr key={r.id}>
                                                <td>{r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "-"}</td>
                                                <td>
                                                    <div>{r.employee_name}</div>
                                                    <div className="pm-history-cc">{r.emp_id}</div>
                                                </td>
                                                <td>
                                                    <div>{r.payslip_number || "-"}</div>
                                                    <div className="pm-history-cc">{r.month_label}</div>
                                                </td>
                                                <td>
                                                    <div>{r.to_email}</div>
                                                    {r.cc_email && <div className="pm-history-cc">Cc: {r.cc_email}</div>}
                                                </td>
                                                <td>
                                                    <span className={`pm-history-status pm-history-status--${(r.status || "").toLowerCase()}`}>
                                                        {r.status}
                                                    </span>
                                                    {r.error_message && <div className="pm-history-err">{r.error_message}</div>}
                                                </td>
                                                <td>{r.sent_by || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="pm-email-foot">
                            <button className="pm-email-btn pm-email-btn--primary" onClick={() => setMailLog(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Download-history modal (mirrors Email History) ── */}
            {downloadHistory && (
                <div className="pm-email-overlay">
                    <div className="pm-email-modal">
                        <div className="pm-email-head">
                            <div>
                                <div className="pm-email-title">
                                    <FaHistory style={{ marginRight: 8 }} />
                                    Download History
                                </div>
                                <div className="pm-email-sub">
                                    {downloadHistory.payslip.payslip_number} · {downloadHistory.payslip.emp_first_name} {downloadHistory.payslip.emp_last_name}
                                    {!downloadHistory.loading && downloadHistory.rows.length > 0 &&
                                        ` · ${downloadHistory.rows.length} download${downloadHistory.rows.length !== 1 ? "s" : ""}`}
                                </div>
                            </div>
                            <button className="pm-email-close" onClick={() => setDownloadHistory(null)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="pm-email-body">
                            {downloadHistory.loading ? (
                                <div className="pm-email-loading">Loading history…</div>
                            ) : downloadHistory.rows.length === 0 ? (
                                <div className="pm-email-loading">
                                    This payslip has not been downloaded yet.
                                </div>
                            ) : (
                                <table className="pm-history-table">
                                    <thead>
                                        <tr>
                                            <th>Downloaded At</th>
                                            <th>By</th>
                                            <th>Period</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {downloadHistory.rows.map(r => (
                                            <tr key={r.id}>
                                                <td>{r.downloaded_at ? new Date(r.downloaded_at).toLocaleString("en-IN") : "-"}</td>
                                                <td>{r.emp_id}</td>
                                                <td>{r.period_label || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="pm-email-foot">
                            <button className="pm-email-btn pm-email-btn--primary" onClick={() => setDownloadHistory(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Email send-history modal ── */}
            {emailHistory && (
                <div className="pm-email-overlay">
                    <div className="pm-email-modal">
                        <div className="pm-email-head">
                            <div>
                                <div className="pm-email-title">
                                    <FaEnvelope style={{ marginRight: 8 }} />
                                    Email History
                                </div>
                                <div className="pm-email-sub">
                                    {emailHistory.payslip.payslip_number} · {emailHistory.payslip.emp_first_name} {emailHistory.payslip.emp_last_name}
                                </div>
                            </div>
                            <button className="pm-email-close" onClick={() => setEmailHistory(null)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="pm-email-body">
                            {emailHistory.loading ? (
                                <div className="pm-email-loading">Loading history…</div>
                            ) : emailHistory.rows.length === 0 ? (
                                <div className="pm-email-loading">No emails have been sent for this payslip yet.</div>
                            ) : (
                                <table className="pm-history-table">
                                    <thead>
                                        <tr>
                                            <th>Sent At</th>
                                            <th>To</th>
                                            <th>Status</th>
                                            <th>By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {emailHistory.rows.map(r => (
                                            <tr key={r.id}>
                                                <td>{r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "-"}</td>
                                                <td>
                                                    <div>{r.to_email}</div>
                                                    {r.cc_email && <div className="pm-history-cc">Cc: {r.cc_email}</div>}
                                                </td>
                                                <td>
                                                    <span className={`pm-history-status pm-history-status--${(r.status || "").toLowerCase()}`}>
                                                        {r.status}
                                                    </span>
                                                    {r.error_message && <div className="pm-history-err">{r.error_message}</div>}
                                                </td>
                                                <td>{r.sent_by || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="pm-email-foot">
                            <button className="pm-email-btn pm-email-btn--primary" onClick={() => setEmailHistory(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Payslip_Management;